import App from './application-container';
import { withProgress, withSpinner } from './state/spinner';
import {
    animate,
    show_reference_partstudio_model,
    init,
    redraw_axes,
    clear_symmetry_plane,
} from './scene';
import * as API from '@phi/frontend/api/calls';
import { read_file_as_text } from '@phi/util';
import { subarray } from '@phi/util/array-buffer';
import { set_button_states } from './non_graphical_ui_actions';
import {
    recalculate_view_dimensions,
    identify_faces_missing_tangencies,
    push_nodups,
    unsuppress_ui_messages_if_ready,
    suppress_ui_messages,
    suppress_animate,
    unsuppress_animate_if_ready,
    animation_is_suppressed,
    suppress_undo_tracking,
    suppress_zero_thickness_checking,
    unsuppress_zero_thickness_checking_if_ready,
    request_setting_button_states,
    update_dom_text_values_and_coordinates,
} from './utility';
import HashTable from './hashtable';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { next_frame, sleep } from '@vaaas/fpts/promise';
import { unique } from '@vaaas/fpts/array';
import { seq, gt, clamp } from '@vaaas/fpts/maths';
import { pipe } from '@vaaas/fpts/function';
import { optimum, map } from '@vaaas/fpts/iter';
import { get_core_side_dragging_state, get_global_id } from './JCore/jcore';
import { clipping_model_editable, clipping_on, destroy_and_hide_clipping_planes } from '../../../../src/features/clipping';
import {
    check_that_user_subscription_is_valid,
    withSuppressedPermissionChecks,
} from './user_authentication';
import * as THREE from 'three';
import { decompressFromEncodedURIComponent } from 'lz-string';
import { show_modal, hide_modal } from './modals';
import * as maths from './maths';
import {
    uid_select_object,
    uid_deselect_all_objects,
    exit_editing_edge_tangent_mode,
    convert_single_reference_face_to_phi,
    handle_escape,
    uid_scene_is_empty,
    uid_splitpatch_faces,
    uid_calculate_global_bounding_box,
    display_toggle_vertex_visibility,
    display_toggle_edge_visibility,
    display_toggle_face_visibility,
} from './ui_display';
import { point_point_distance } from './vector3';
import { WrappedValue } from '@phi/wrapped-value';
import { face_is_for_sketch } from './reference_sketches';
import {
    show_import_troubleshooting_modal,
    show_boolean_dialog,
} from './state/modals';
import {
    can_save_to_onshape,
    can_save_locally_or_to_onshape,
    model_is_dirty,
    deselect_onshape_document,
    has_selected_document,
    is_connected_to_onshape,
} from './state';
import triangles from './state/triangles';
import {
    name_without_counter,
    recommend_different_filename,
    without_file_extension,
} from '@phi/util/file-name';
import { uil_get_world_scaling_factor, uil_is_object_visible, uil_load_model } from './ui_logic';
import { read_number } from '@phi/util/typed-array';
import { triangle_normal } from './geometry_and_math';
import { Uid_ref_edge, Uid_ref_face, Uid_ref_face_triangle, Uid_ref_face_vertex, Uid_ref_vertex } from './graph_objects';
import { make_selected_coordinates_invisible } from '../../../../src/features/indicate-vertices';
import { curvature_edges, hide_edge_curvatures } from '../../../../src/features/curvature-combs';
import { frontend_service_container } from '@phi/frontend/service-container';
import { download_as_file } from '@phi/util/dom';
import * as error_messages from '@phi/data/error-messages';
import { reference_transparent } from '../../../../src/features/references';

export async function onshape_connection_is_healthy() {
    const data = await API.health();
    if (data instanceof Error) {
        display_message_to_user(LOG.WARN, "Connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref2]");
        return false;
    } else if (data && data.health == 'ok' && data.onshape == 'ok') {
        return true;
    } else {
        display_message_to_user(LOG.WARN, "Connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref1]");
        return false;
    }
}

export async function refresh_model_modal() {
  if (!model_is_dirty() || uil_get_model_bodies() === null || uil_get_model_bodies().length == 0) {
    refresh_model();
  }
  else {
    //if (App.state.framework == 'onshape') {
    if (can_save_to_onshape()) {
        await frontend_service_container().saving_loading.onshape_autosave();
    }
    show_modal("refreshModal");
  }
}

export async function refresh_model() {
  App.state.current_filename = '';
  handle_escape();
  clear_symmetry_plane();
  if (App.state.triangles != 1600)
    triangles(1600); // set triangles back to the defaults
  App.state.display_units = App.settings.display_units;
  deselect_onshape_document();
  await next_frame();

  // Reset exporting model state flag
  App.state.currently_exporting_model = false;

  // Exit edit tangent mode
  exit_editing_edge_tangent_mode(true);

  // Clear any clipping
  uid_state.clipping_has_been_set = false;
  clipping_model_editable.value = false;
  clipping_on.value = false;
  uid_state.clipping_plane_selected = "";
  destroy_and_hide_clipping_planes();

  display_clear_model();
  if (curvature_edges.value.length > 0)
    hide_edge_curvatures();
  curvature_edges.value = [];

  // Clear text on screen
  for (const x of dom_text_elements.values())
    x.remove_text_from_dom();

  if (App.state.currently_hiding_edges)
    display_toggle_edge_visibility();

  if (App.state.currently_hiding_faces)
    display_toggle_face_visibility();

  if (App.state.currently_hiding_vertices)
    display_toggle_vertex_visibility();

  await sleep(400);
  init(false, App.state.tut_stage);
}

window.makeTextFile = function(text) {
  var data = new Blob([text], { type: "text/plain" });

  // If we are replacing a previously generated file we need to manually revoke the object URL to avoid memory leaks.
  if (textFile !== null) {
    window.URL.revokeObjectURL(textFile);
  }

  textFile = window.URL.createObjectURL(data);

  return textFile;
};

export function uid_download() {
  if (!can_save_locally_or_to_onshape())
    display_message_to_user(LOG.INFO, "Cannot save model.");
  else
    show_modal("savePhiModelModal");
}

export function uid_upload() {
  hide_modal("selectModelModal");
  setTimeout(function() {
    show_modal("UploadModal");
  }, 200);
}

export function show_export_modal() {
    show_modal("ExportModal");
}

export function show_export_to_onshape_modal() {
  if (!can_save_to_onshape())
    return display_message_to_user(LOG.INFO, "Cannot export model to Onshape.");

  show_modal("ExportToOnshapeModal");
}

export const save_phi_model_to_onshape = withSuppressedPermissionChecks(async function save_phi_model_to_onshape(
  phi_model_description_string,
  autosave = false,
  copy_on_load = false,
  show_spinner = true,
) {
    function handle_error(error) {
        App.state.spinner = undefined;
        App.state.file_save_status = 'dirty';
        console.log("Failed to save file to Onshape.");
        console_log_xml("Failed to save file to Onshape.");
        if (error instanceof Error)
          display_message_to_user(LOG.WARN, error_messages.failed_to_save_file_to_onshape);
    }

    if (App.state.file_save_status == 'saving') {
      // A save is currently underway.
      if (!autosave)
        display_message_to_user(LOG.WARN, "File is currently being saved, please try again in a moment");
      return;
    }

    // If there's no valid subscription, don't proceed with save.
    if (!check_that_user_subscription_is_valid())
        return;

    if (show_spinner)
        App.state.spinner = 'Saving file to Onshape';

    // First check what Phi models are currently stored in the document as blobs
    const array_of_elements = await API.listDocumentElements(App.state.document, App.state.workspace, false);
    if (array_of_elements instanceof Error)
        return handle_error(array_of_elements);

    // If what we got back is not an array, don't proceed with the save since
    // we probably lost connection with Onshape.
    if (!array_of_elements || !Array.isArray(array_of_elements)) {
        console.log("Received inappropriate response from Onshape to list_parts request while in save_phi_model_to_onshape.");
        display_message_to_user(LOG.WARN, error_messages.failed_to_save_file_to_onshape);
        App.state.spinner = undefined;
        return;
    }

    // save was triggered when the user has not selected any file name
    // give the file an appropriate, unique name
    if (App.state.current_filename.length === 0) {
        const names = array_of_elements
            .filter(x => x.elementType === 'BLOB')
            .map(x => without_file_extension(x.name));
        App.state.current_filename =
            names.includes(default_file_name)
                ? recommend_different_filename(default_file_name, names)
                : default_file_name;
    }

    var onshape_tab_name = App.state.current_filename;

    const should_save_file =
        App.settings.save_backups
        && (
            !window.loading_into_current_model
            || uid_scene_is_empty()
        );

    if (copy_on_load && should_save_file) {
        const reg = /\//g;
        const dstr = new Date().toLocaleString().replace(reg, '-');
        if (onshape_tab_name.endsWith(".phi") == 0) {
            onshape_tab_name = "_" + onshape_tab_name + ".backup." + dstr + ".phi";
        }
        else {
            var onshape_tab_nameNew = onshape_tab_name.substring( 0, onshape_tab_name.length - 4 );
            onshape_tab_name = onshape_tab_nameNew;
            onshape_tab_name = "_" + onshape_tab_name + ".backup." + dstr + ".phi";
        }
    }

    // Add .phi at end of tab name if it doesn't exist.
    if (onshape_tab_name.endsWith(".phi") == 0)
        onshape_tab_name += ".phi";

    const existingFile = array_of_elements.find((e) =>
        e.elementType === "BLOB"
        && (
            e.name === onshape_tab_name
            || e.name + ".phi" === onshape_tab_name
        )
    );

    if (!phi_model_description_string)
        phi_model_description_string = uid_save_model(save_compression_level, 1.0);

    // We perform URI encoding which makes sure our string is safe to send over HTTP.
    App.state.file_save_status = 'saving';
    const response = await API.sendPhiModelToOnshape(
        App.state.document,
        existingFile ? existingFile.id : undefined,
        App.state.workspace,
        onshape_tab_name,
        phi_model_description_string,
    );
    if (response instanceof Error)
        return handle_error(response);

    if (autosave) {
        console.log("Autosaved to Onshape at " + Date().toLocaleString());
        uid_unset_dirty_flag("to_onshape_autosave");
    } else if (copy_on_load) {
        console.log("Saved copy to Onshape on load at " + Date().toLocaleString());
        uid_unset_dirty_flag("load_model");
    } else {
        API.log('Save model to Onshape as Phi file', "Save model to Onshape as Phi file");
        display_message_to_user(LOG.INFO, "Model successfully saved in Onshape tab.");
        console.log("Saved to Onshape at " + Date().toLocaleString());
        uid_unset_dirty_flag("to_onshape");
    }

    if (App.state.spinner)
        App.state.spinner = undefined;
});

export function ui_import_ref_stl_model() {
  show_modal("ImportRefStlModelModal");
}

export async function performRequestsForDescription(element_id, element_name, part_id, part_name, as_reference, is_partstudio) {
  const reqs = is_partstudio
    ? [
      API.getPartStudioBodyDetails(App.state.document, element_id, App.state.workspace, true),
      API.getPartStudioTessellatedEdges(App.state.document, element_id, App.state.workspace),
      API.getPartStudioTessellatedFaces(App.state.document, element_id, App.state.workspace),
    ]
    : [
      API.getPartBodyDetails(App.state.document, element_id, part_id, App.state.workspace),
      API.getPartTessellatedEdges(App.state.document, element_id, part_id, App.state.workspace),
      API.getPartTessellatedFaces(App.state.document, element_id, part_id, App.state.workspace),
    ];

  const response = await Promise.all(reqs)

  if (response.some(x => x instanceof Error)) {
    console_log_xml("Failed to receive Part/Part Studio description from Onshape. Perhaps connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref7]");
    display_message_to_user(LOG.WARN, "Failed to receive Part/Part Studio description from Onshape. Perhaps connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref7]");
  }
  else {
    uid_load_onshape_partstudio_body_details_model(element_id, element_name, part_id, part_name, as_reference, is_partstudio, response[0], response[1], response[2]);
  }
}

export const request_partstudio_body_details = withSpinner('Loading reference body details', async function request_partstudio_body_details(element_id, element_name, part_id, part_name, as_reference, is_partstudio) {
  // We assume that the the Part ids take the values JHD, JKD .. and the
  // Part names Part 1, Part 2 ...
  // We do not need to check if the part name or part id has changed since Onshape uses
  // JHD, JKD ... as part ids and Part 1, Part 2 ... as part names.
  // When a workspace is copied the part names and ids do not change.
  const data = await API.listDocumentElements(App.state.document, App.state.workspace, false);

  if (data instanceof Error)
    return console.error('connection with onshape has been lost');

  // We check if the element id is valid
  const matchingElementId = data.find(x => (x.elementType === "PARTSTUDIO") && (x.id === element_id));
  if (matchingElementId) {
    await performRequestsForDescription(element_id, element_name, part_id, part_name, as_reference, is_partstudio);
    return;
  }

  // In case the ids are mismatched we match by name
  const matchingElementName = data
    .find(x => x.elementType === "PARTSTUDIO" && x.name === element_name);

  console.log("Mismatched ids");
  // In case we werent able to match the element name, we display an error
  if (!matchingElementName) {
    console_log_xml("Failed to receive Part/Part Studio description from Onshape. Perhaps connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref8]");
    display_message_to_user(LOG.WARN, "Failed to receive Part/Part Studio description from Onshape. Perhaps connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref8]");
    return ;
  }

  // We perform new requests but this time with the correct id
  const newId = matchingElementName.id;
  await performRequestsForDescription(newId, element_name, part_id, part_name, as_reference, is_partstudio);
});

export const loadPhiModelFromOnshape = withSpinner('Loading Phi model from Onshape', async function loadPhiModelFromOnshape(selected_element_id, selected_element_name, should_hide_modal = true) {
    API.log('Load Phi model from Onshape Document', "Load Phi model from Onshape Document");
    const data = await API.loadPhiModelFromOnshape(App.state.document, selected_element_id, App.state.workspace);

    if (data instanceof Error) {
        console_log_xml("Failed to load Phi model from Onshape");
        display_message_to_user(LOG.WARN, "Failed to load Phi model from Onshape. Perhaps connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref9]");
    }

    const is_old_format = !(data.charAt(0) == "P" && data.charAt(1) == "h" && data.charAt(2) == "i")
    const is_autosaved_backup_file = selected_element_name.charAt(0) === "_";

    const ru = is_old_format
        ? decompressFromEncodedURIComponent(data)
        : data;

    if (!window.loading_into_current_model)
        App.state.current_filename = without_file_extension(selected_element_name);

    await uid_load_model(ru, false, false, false, false, false);

    if (should_hide_modal) {
        hide_modal("selectModelModal");
    }

    // Autosave a backup copy of the file we just opened to Onshape, unless it was an autosaved file
    // and unless we're loading into the current (non-empty!) model
    // And of course as long as App.settings.save_backups is true.
    const should_save_file =
        App.settings.save_backups
        && (
            !window.loading_into_current_model
            || uid_scene_is_empty()
        );

    if (!is_autosaved_backup_file
        && uid_vertices.keys().length > 0
        && get_core_side_dragging_state() == "off"
        && !App.state.currently_exporting_model
        && should_save_file
    )
        save_phi_model_to_onshape(null, false, true, false);
    else if (is_autosaved_backup_file)
        display_message_to_user(LOG.WARN, "You loaded a restored backup file. We recommend you save it right away with a new filename.");
});

export function save_model(fileNameToSaveAs, saveMethod) {
  if (!fileNameToSaveAs.endsWith('.phi'))
    fileNameToSaveAs += ".phi";
  const phi_model_description_string = uid_save_model(save_compression_level, 1.0);

  switch (saveMethod) {
    case 'onshape':
        return save_model_in_phi_format_to_onshape(fileNameToSaveAs, phi_model_description_string);
    case 'local':
        return save_model_in_phi_format_to_file(fileNameToSaveAs, phi_model_description_string)
  }
}

export async function save_model_in_phi_format_to_file(fileNameToSaveAs, phi_model_description_string) {
    downloadString(phi_model_description_string, fileNameToSaveAs)
    uid_unset_dirty_flag("to_file");
    //App.state.current_filename = without_file_extension(fileNameToSaveAs);
}

export async function save_model_in_phi_format_to_onshape(onshape_tab_name, phi_model_description_string) {
    //if (App.state.framework !== 'onshape') {
    if (!can_save_to_onshape()) {
        display_message_to_user(LOG.WARN, "Onshape connection not detected.");
        return;
    }

    // First check what Phi models are currently stored in the document as blobs
    const data = await API.listDocumentElements(App.state.document, App.state.workspace, false);
    if (data instanceof Error) {
        console_log_xml("Connection with Onshape has been lost when calling list-document-elements");
        display_message_to_user(LOG.WARN, error_messages.failed_to_save_file_to_onshape);
        return;
    }

    const existingFile = data.find(e =>
        e.elementType === "BLOB"
            && (
                e.name === onshape_tab_name
                || e.name + ".phi" === onshape_tab_name
            ));

    if (!existingFile || (existingFile && await show_boolean_dialog({
        title: 'Overwrite file',
        body: 'You are about to overwrite a saved file. Are you sure you want to proceed? You can click cancel and repeat with a different filename instead.',
        ok: 'Proceed with save'
    }))) {
        App.state.current_filename = without_file_extension(onshape_tab_name);
        save_phi_model_to_onshape(phi_model_description_string, false, false, true);
    }
}

function downloadString(string, name) {
    const fileAsBlob = new Blob([string], { type: 'application/octet-stream' });
    const anchor = document.createElement('a');
    anchor.download = name;
    anchor.innerHTML = 'Download File';
    anchor.href = URL.createObjectURL(fileAsBlob);
    anchor.onclick = destroyClickedElement;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
}

export async function save_copy_of_phi_file_that_was_just_loaded_to_onshape() {
    if (!can_save_to_onshape())
        return;

    // First check what Phi models are currently stored in the document as blobs
    const files = await API.listDocumentElements(App.state.document, App.state.workspace, false);
    if (files instanceof Error) {
        console_log_xml("Connection with Onshape has been lost when calling list-document-elements");
        display_message_to_user(LOG.WARN, error_messages.failed_to_save_file_to_onshape);
        return;
    }

    const existing_files = files
        .filter(x => x.name.endsWith('.phi'))
        .map(x => without_file_extension(x.name));

    if (existing_files.includes(App.state.current_filename)) {
        App.state.current_filename = recommend_different_filename(name_without_counter(App.state.current_filename), existing_files);
        display_message_to_user(LOG.WARN, `We updated your filename to ${App.state.current_filename } to avoid a clash with an existing .phi file tab.`);
    }

    const phi_model_description_string = uid_save_model(save_compression_level, 1.0);
    save_phi_model_to_onshape(phi_model_description_string, false, false, false);
    if (App.settings.save_backups)
      save_phi_model_to_onshape(phi_model_description_string, false, true, false);
}

export function saveStlTextAsFile(triangles_per_face, file_name) {
  // First we clear the hash that contains the STL surface data
  stl_surfaces_hash.clear();

  if (frontend_service_container().subscriptions.state.subscription_status.value == 'free' && triangles_per_face > 300) {
    triangles_per_face = 300;
    display_message_to_user('STL export limited to 300 triangles for this plan. Please upgrade to Pro for full STL resolution');
  }

  // Call the core to have it re-fill the STL surfaces hash with data about the selected (or all) surfaces
  const face_ids = uid_faces.keys().filter(k =>
        uil_is_object_visible(k)
        && !face_is_for_sketch(k)
        && (
            uid_selected_faces.length == 0
            || uid_selected_faces.hasItem(k)
        )
    );

  uil_triangulate_faces(face_ids, clamp(20, 20000)(triangles_per_face));

  download_as_file(
    file_name + '.STL',
    'text/plain',
    create_STL()
  );
}

export function create_STL() {
  var s = "solid PhiSTL\n";
  stl_surfaces_hash.values().forEach(function(f) {
    f.faces.forEach(function(tf) {
      var v1 = new THREE.Vector3(
        f.vertices[tf.a].x,
        f.vertices[tf.a].y,
        f.vertices[tf.a].z
      );
      var v2 = new THREE.Vector3(
        f.vertices[tf.b].x,
        f.vertices[tf.b].y,
        f.vertices[tf.b].z
      );
      var v3 = new THREE.Vector3(
        f.vertices[tf.c].x,
        f.vertices[tf.c].y,
        f.vertices[tf.c].z
      );

      var n = triangle_normal(v1, v2, v3);
      s += "  facet normal " + n.x + " " + n.y + " " + n.z + "\n";
      s += "    outer loop\n";
      s += "      vertex " + v1.x + " " + v1.y + " " + v1.z + "\n";
      s += "      vertex " + v2.x + " " + v2.y + " " + v2.z + "\n";
      s += "      vertex " + v3.x + " " + v3.y + " " + v3.z + "\n";
      s += "    endloop\n";
      s += "  endfacet\n";
    });
  });
  s += "endsolid PhiSTL\n";
  return s;
}


export function destroyClickedElement(event) {
  document.body.removeChild(event.target);
}

export const loadFileAsText = withSpinner('Loading file', async (fileToLoad) => {
    hide_modal('selectModelModal');
    await sleep(250);

    const loadedFile = await read_file_as_text(fileToLoad);

    // Check if it's an old format file
    const is_old_format = !(
        loadedFile.charAt(0) == "P"
        && loadedFile.charAt(1) == "h"
        && loadedFile.charAt(2) == "i"
    );

    if (is_old_format) {
        let loadedFileUnzipped = decompressFromEncodedURIComponent(loadedFile);
        if (loadedFileUnzipped) {
            await uid_load_model(loadedFileUnzipped, true, true, false, true, false);
        } else {
            display_message_to_user(LOG.WARN, "Error loading file.")
            return;
        }
        loadedFileUnzipped = null;
    } else {
        await uid_load_model(loadedFile, true, true, false, true, false);
    }

    const should_save_file =
        !window.loading_into_current_model
        || uid_scene_is_empty();

    if (should_save_file) {
        App.state.current_filename = without_file_extension(fileToLoad.name);
        console.log(App.state.current_filename);
        if (!has_selected_document() && is_connected_to_onshape()) {
            const doc = await API.createDocument(App.state.current_filename);
            App.state.document = doc.id;
            App.state.workspace = doc.defaultWorkspace.id;
        }
        if (can_save_to_onshape()) {
            // Check whether a file with this name already exists and if so take measures, then save to Onshape
            save_copy_of_phi_file_that_was_just_loaded_to_onshape();
        }
    }
});

export const loadRefSTLOnshape = withSpinner('Loading STL references from Onshape', async function loadRefSTLOnshape(elementId, documentId, workspaceId, ref_model_units, flip_y_z, force_load_reference_vertices = false) {
    const isASCII = str => /^[\x00-\x7F]*$/.test(str);

    // We use an xhr request instead of ajax since ajax doesnt support
    // the arraybuffer dataType
    const response = await API.blob_stream({ document: documentId, element: elementId, workspace: workspaceId });
    if (response instanceof Error) {
        display_message_to_user(LOG.WARN, "Failed to load reference STL model. Perhaps the format is wrong?");
        throw "Error loading STL file";
    }

    update_dom_text_values_and_coordinates();

    const arrayBuffer = new Uint8Array(response);

    console_log("Display units:", App.state.display_units);
    console_log("Reference model units:", ref_model_units);

    // Test if the string is binary and override given flag value.
    const enc = new TextDecoder("utf-8");
    const test_str = enc.decode(arrayBuffer) ;
    const is_binary = !isASCII(test_str);

    const str_array = [];
    if (is_binary) {
        var str = new String("");
        str += "solid PhiSTL\n";
        str += await load_some_stl_triangles(arrayBuffer, str, str_array);
        str += "endsolid PhiSTL\n";

        const new_string_to_insert = new String(str);
        str_array.push(new_string_to_insert);
        str = "";

        update_dom_text_values_and_coordinates();

        complete_loading_ref_STL_model_from_Onshape(
            str_array,
            elementId,
            documentId,
            workspaceId,
            ref_model_units,
            flip_y_z,
            force_load_reference_vertices,
        );
    } else {
        const enc = new TextDecoder("utf-8");
        const str = enc.decode(arrayBuffer) ;
        str_array.push(str);
        complete_loading_ref_STL_model_from_Onshape(
            str_array,
            elementId,
            documentId,
            workspaceId,
            ref_model_units,
            flip_y_z,
        );
    }
});

async function load_some_stl_triangles(arrayBuffer, str, str_array) {
    /** Skip 80 bytes of header */
    const header_offset = 80;

    /** Read number of triangles in a 32 bit unsigned integer, starting at byte 80 */
    const n_triangles = read_number(arrayBuffer, 4, 8, header_offset, false);

    //console_log("n_triangles:", n_triangles);
    let triangles_processed = 0;
    let triangles_in_array = [];
    let arrayBuffer_length = header_offset + 4 + n_triangles * 50;
    const bufferView = new Int8Array(arrayBuffer);

    await withProgress('Reading STL triangles', (i10k) => {
        const last_triangle_to_process = Math.min(n_triangles - i10k * 10_000, 10_000);

        for (let i = 0; i < last_triangle_to_process; i++) {
            const idx = i10k * 10_000 + i;
            const sub = subarray(bufferView, header_offset +  4 + idx * 50, 50);
            const ar = new Float32Array(sub, 0, 12);
            triangles_in_array.push(ar);
        }
    })(Array.from(seq(0, Math.floor(n_triangles / 10_000))));

    await withProgress('Writing STL triangles', (i10k) => {
      const last_triangle_to_process = Math.min(n_triangles - i10k * 10_000, 10_000);

      for (let i = 0; i < last_triangle_to_process; i++) {
          const idx = i10k * 10_000 + i;
          let str1 = "";
          str1 += "  facet normal ";

          // Read 12 32bit float numbers (and discard the rest up to 50 bytes)
          for (let j = 0; j < 3; j++) {
              str1 += triangles_in_array[idx][j];
              str1 += " ";
          }
          str1 += "\n";
          str1 += "    outer loop\n";

          for (let j = 0; j < 3; j++) {
              str1 += "      vertex ";
              for (let k = 0; k < 3; k++) {
                  str1 += triangles_in_array[idx][3 * j + 3 + k];
                  str1 += " ";
              }
              str1 += "\n";
          }
          str1 += "    endloop\n";
          str1 += "  endfacet\n";
          str += str1;

          if ((idx > 0) && (idx % 1_000_000 === 0)) {
              str_array.push(str);
              str = "";
          }

          triangles_processed++;
      }
  })(Array.from(seq(0, Math.floor(n_triangles / 10_000))));

    //console.log("Triangles processed: " + triangles_processed);
    return str;
}

export function complete_loading_ref_STL_model_from_Onshape(str_array, elementId, documentId, workspaceId, ref_model_units, flip_y_z, force_load_reference_vertices = false) {
  var input_factor = 1;
  if (ref_model_units == "cm")
    input_factor = 1;
  else if (ref_model_units == "ft")
    input_factor = 30.479999953;
  else if (ref_model_units == "in")
    input_factor = 2.54;
  else if (ref_model_units == "m")
    input_factor = 100;
  else if (ref_model_units == "mm")
    input_factor = 0.1;
  else if (ref_model_units == "yd")
    input_factor = 91.44;

  var factor = input_factor;

  // Set world_scaling_factor if unset
  if (uil_get_world_scaling_factor() == 0) {
    // Find bounding box of STL file to be loaded in unitless values.
    var d = get_bounding_box_of_stl_file(str_array);
    // Transform the bounding box dimensions based on selected STL units
    d *= factor;
    uil_set_world_scaling_factor(d);
  }

  factor /= uil_get_world_scaling_factor();
  //console.log(str);
  // Load the STL file.
  var succeeded = uid_load_ref_stl_model(str_array, factor, flip_y_z, elementId, force_load_reference_vertices);

  if (succeeded) {
    App.state.imported_stl_models.push(
      {
        "documentId" : documentId,
        "workspaceId": workspaceId,
        "elementId": elementId,
        "units": ref_model_units,
        "isFlipped": flip_y_z
      }
    );
    last_imported_stl_units = ref_model_units;
  }
  set_button_states();
}

let loading = false;
export async function loadGivenModelString(props, should_create_document = false) {
    if (!global_default_model_data) {
        console.log("Error loading default model");
        return;
    }

    const should_save_file =
        !window.loading_into_current_model
        || uid_scene_is_empty();

    if (loading) return;
    loading = true;

    // Read given dimensions and units from interface.
    global_default_model_data.model = props.selectedModel;
    global_default_model_data.dx = props.x;
    global_default_model_data.dy = props.y;
    global_default_model_data.dz = props.z;
    global_default_model_data.height = props.height;
    global_default_model_data.diameter = props.diameter;
    global_default_model_data.units = props.units;
    App.state.display_units = props.units;

    if (should_create_document) {
        const response = await API.createDocument(props.file_name);
        if (response instanceof Error)
            display_message_to_user(LOG.ERROR, 'Could not create Onshape Document because the connection with Onshape has been lost.');
        else {
            App.state.document = response.id;
            App.state.workspace = response.defaultWorkspace.id;
        }
    }

    const loadedFile = await fetch(`/models/${global_default_model_data.model}.phi`).then(x => x.text());

    await uid_load_model(loadedFile, true, true, true, true, true);
    animate();

    if (should_save_file)
        App.state.current_filename = props.file_name;
    if (can_save_to_onshape() && should_save_file) {
      // Check whether a file with this name already exists and if so take measures, then save to Onshape
      save_copy_of_phi_file_that_was_just_loaded_to_onshape();
    }

    loading = false;
}

export async function uid_load_model(str,
    should_hide_modals = true,
    must_show_spinner = true,
    is_default_model = false,
    viewall = true,
    must_handle_missing_tangencies = true,
) {
  if (App.state.currently_loading_model)
    return;
  App.state.currently_loading_model = true;

  // Read settings reference_transparent value before proceeding with load.
  reference_transparent.value = App.settings.reference_transparent;
  //console.log("In uid_load_mode, setting reference_transparent to...");
  //console.log(reference_transparent.value);

  suppress_animate();
  suppress_undo_tracking();

  // Remove any coordinates text
  make_selected_coordinates_invisible();

  if (should_hide_modals) {
    hide_modal('UploadModal');
    hide_modal('selectModelModal');
  }

  await perform_load_model(str, is_default_model);

  if (must_handle_missing_tangencies)
    handle_missing_tangencies();

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  if (!animation_is_suppressed()) {
    request_setting_button_states();
    if (viewall)
      await view_all(false);
    animate();
    setTimeout(async () => {
      recalculate_view_dimensions(false);
      redraw_axes();
      await sleep(4000);
      recalculate_view_dimensions(false);
    }, 1000);
  }
}

export function handle_missing_tangencies() {
  // We will skip this step pending further testing.
  return;

  var faces_missing_tangencies = identify_faces_missing_tangencies();

  if (faces_missing_tangencies.length > 0) {
    faces_missing_tangencies.forEach(function(fid){
      uid_select_object(fid);
    });
    var face_text = faces_missing_tangencies.length > 1 ? "faces that have" : "face that has";
    display_message_to_user(LOG.WARN, "We identified " + faces_missing_tangencies.length + " " + face_text +
                            " edges that should be marked as tangent but are not." +
                            " We have split these faces manually");

    // Proceed with splitting these faces (For now don't wait for confirmation)
    uid_deselect_all_objects();

    /* To split on vertices */
    uid_splitpatch_faces(faces_missing_tangencies, true, true, true);

    /* To split on edges - not a good idea as we may still have problems
    uid_splitpatch_faces(faces_missing_tangencies, false, true, true);
    */

    // Then flatten any faces that need flattening (but probably not needed)
  }
}

export async function perform_load_model(str, is_default_model) {
    await uil_load_model(str, is_default_model);
    const bb = uid_calculate_global_bounding_box(true, false, false);
    uid_ball_size = uid_default_ball_size * bb.dimension;
    animate();
}

export function uid_save_model(compression_level, extra_data_fac = 1.0) {
    const s = uil_save_model(compression_level, extra_data_fac);
    update_dom_text_values_and_coordinates();
    return s;
}

/*
export function uid_set_file_model_coordinate_factor(factor) {
  return uil_set_file_model_coordinate_factor(factor);
}
*/

export function get_bounding_box_of_stl_file(str_array) {
  var minx =999999, maxx=-999999, miny=999999, maxy=-999999, minz=999999, maxz=-999999;

  str_array.forEach(function(str) {
    var rows = str.split(/\r?\n/);
    for (var i = 0 ; i < rows.length; i++) {
      var row = rows[i];
      // If row contains vertex coordinates...
      if (row.indexOf("vertex") != -1) {
        var row_elements = [];
        row_elements = row.trim().split(/ /);
        if (parseFloat(row_elements[1]) > maxx)
          maxx = parseFloat(row_elements[1]);
        if (parseFloat(row_elements[1]) < minx)
          minx = parseFloat(row_elements[1]);
        if (parseFloat(row_elements[2]) > maxy)
          maxy = parseFloat(row_elements[2]);
        if (parseFloat(row_elements[2]) < miny)
          miny = parseFloat(row_elements[2]);
        if (parseFloat(row_elements[3]) > maxz)
          maxz = parseFloat(row_elements[3]);
        if (parseFloat(row_elements[3]) < minz)
          minz = parseFloat(row_elements[3]);
      }
    }
  });
  return Math.max(Math.abs(maxx-minx), Math.abs(maxy-miny), Math.abs(maxz-minz));
}

export function uid_load_ref_stl_model(str_array, factor, flip_y_z, elementId, force_load_reference_vertices = false) {

  // Array of reference vertices
  var succeeded = true;
  var reference_vertices = [];
  var reference_triangles = [];
  var reference_vertices_hash = new HashTable();
  uid_reference_vertex_coords_hash.clear();

  // Create a new body ID
  var body_id = elementId;

  // Parse string to load model.
  var rows = [];
  str_array.forEach(function(str){
    var newrows = str.split(/\r?\n/);
    rows = rows.concat(newrows);
  });
  var next_triangle_vertex_to_read = 0;
  var refv1, refv2, refv3;

  if (rows.length > 200000 && !force_load_reference_vertices) {
    do_not_load_reference_vertices = true;
    display_message_to_user(
      LOG.INFO,
      "Due to the large number of triangles in imported STL file, snapping to reference vertices is disabled."
    );
  }

  for (var i = 0 ; i < rows.length; i++) {
    var row = rows[i];

    if ( i % 10000 == 0)
      console_log("Row " + i + " of " + rows.length);

    // If row contains facet, we have a new triangle.
    if (row.indexOf("facet") != -1 && row.indexOf("endfacet") == -1) {
      next_triangle_vertex_to_read = 0;
    }

    // If contains vertex coordinates,add reference vertex.
    else if (row.indexOf("vertex") != -1) {
      // New vertex coordinates found
      var row_elements = [];
      row_elements = row.trim().split(/ /);

      var x = row_elements[1] * factor;
      var y = row_elements[2] * factor;
      var z = row_elements[3] * factor;

      var refv;
      if (!flip_y_z)
        refv = new THREE.Vector3( x, y, z );
      else
        refv = new THREE.Vector3( x, z * (-1), y );
      var refvObj = new Uid_ref_vertex(0, refv, body_id, "stl");

      // Add the vertex to the scene, unless there is already a vertex at the same coordinates
      // (within an epsilon)

      var vertex_found = false;
      if (!do_not_load_reference_vertices) {
        var str =
          Math.round(refv.x * 1000000).toString() +
          Math.round(refv.y * 1000000).toString() +
          Math.round(refv.z * 1000000).toString();
        if (uid_reference_vertex_coords_hash.hasItem(str)) vertex_found = true;
      }

      if (!vertex_found && !do_not_load_reference_vertices) {
        uid_add_reference_vertex(refvObj, false);
      }

      // Add vertex to hash
      var refvkey;
      if (!flip_y_z)
        refvkey = x + ";" + y + ";" + z;
      else
        refvkey = x + ";" + z * (-1) + ";" + y;
      if (!reference_vertices_hash.hasItem(refvkey))
        reference_vertices_hash.setItem(refvkey, refvObj);

      if (next_triangle_vertex_to_read == 0) refv1 = refv;
      else if (next_triangle_vertex_to_read == 1) refv2 = refv;
      else refv3 = refv;
      next_triangle_vertex_to_read++;
    }

    // If row contains endfacet, we have a new triangle.
    else if (row.indexOf("endfacet") != -1) {
      reference_triangles.push(
        new Uid_ref_face_triangle(0, 0, 0, refv1, refv2, refv3)
      );
      next_triangle_vertex_to_read = 0;
    }
  }

  // Now go through reference_vertices_hash and puth them in an array and also add array index in hash elements.
  reference_vertices_hash.values().forEach(function(v) {
    reference_vertices.push(v.vertex);
    var i = reference_vertices.length - 1;
    v.vindex = i;
  });

  // Now go through triangles and replace coordinates with vertex indices
  reference_triangles.forEach(function(t) {
    var refvkey = t.vertex1.x + ";" + t.vertex1.y + ";" + t.vertex1.z;
    var v = reference_vertices_hash.getItem(refvkey);
    t.index1 = v.vindex;

    var refvkey = t.vertex2.x + ";" + t.vertex2.y + ";" + t.vertex2.z;
    var v = reference_vertices_hash.getItem(refvkey);
    t.index2 = v.vindex;

    var refvkey = t.vertex3.x + ";" + t.vertex3.y + ";" + t.vertex3.z;
    var v = reference_vertices_hash.getItem(refvkey);
    t.index3 = v.vindex;
  });

  // Check that we were able to load the model
  if (reference_vertices.length == 0 || reference_triangles.length == 0) {
    display_message_to_user(LOG.WARN, "Failed to load reference STL model. Perhaps the format is wrong?");
    succeeded = false;
  }
  else {
    // Add the surface

    var ref_face_obj = new Uid_ref_face( body_id, reference_vertices, reference_triangles, null, true, body_id, "stl");

    uid_add_reference_face(ref_face_obj);

    uid_reference_stl_raynode.add(uid_ref_stl_faces_raynode);

    // Resize all reference vertices
    resize_all_reference_vertices_for_zoom(0, viewSize, 0);

    var bb = uid_calculate_global_bounding_box(true, false, false);
    uid_ball_size = uid_default_ball_size * bb.dimension;

    App.state.showing_reference_stl = true;
    view_all(false);
  }

  hide_modal("ImportRefStlModelModal");
  request_setting_button_states();
  animate();
  return succeeded;
}

export async function uid_load_onshape_partstudio_body_details_model(
    onshape_element_id,
    onshape_element_name,
    onshape_part_id,
    onshape_part_name,
    as_reference,
    is_partstudio,
    body_details_call1_response,
    body_details_call2_response,
    body_details_call3_response,
) {
  const body_details = body_details_call1_response || null;
  if (!body_details) {
    display_message_to_user(LOG.WARN, "Import failed");
    return;
  }

  // Register this in the list of loaded reference models if it doesn't exist there yet.
  var found = false;
  App.state.imported_partstudio_reference_models.forEach(function (irm){
    if (irm.element_id == onshape_element_id && irm.part_id == onshape_part_id)
      found = true;
  });
  if (!found) {
    // Create a new record for this part and add it to the array.
    const irm = {
        "element_id": onshape_element_id,
        "element_name": onshape_element_name,
        "part_id": is_partstudio ? "" : onshape_part_id,
        "part_name": is_partstudio ? "" : onshape_part_name,
        "is_partstudio": is_partstudio,
    };
    App.state.imported_partstudio_reference_models.push(irm);
  }

  //Always display reference vertices
  do_not_load_reference_vertices = false;
  var json_obj_bd = body_details;

  if (!as_reference && ((!json_obj_bd) || (!json_obj_bd.bodies) || (json_obj_bd.bodies.length == 0))) {
    display_message_to_user(LOG.WARN,
      "Model doesn't contain any body descriptions that can be imported to Phi (it may consist of only tesselated faces). Will proceed to import any faces as reference");
      as_reference = true;
    //return;
  }

  // Set the name of the Phi model if it's not as reference
  if (!onshape_element_name) onshape_element_name = "";

  // Before proceeding with the import, calculate a "bounding box" dimension and
  // NOTE: Tessellation coordinates are in meters, and so is the bounding box calculated below.
  var d = get_onshape_imported_model_bounding_box(json_obj_bd);

  /*
  if (d < 1) {
    var tessellated_faces = body_details_call3_response;
    get_onshape_imported_model_tessellation_bounding_box(tessellated_faces);
  }
  */

  // Set a factor to take account of current model scale, or set current model scale
  let factor;
  let must_bring_up_warning_window = false;
  const props = {
    title: 'Importing model',
    ok: 'Proceed',
    cancel: 'Cancel',
    body: '',
  };
  if (uil_get_world_scaling_factor() > 0) {
    // The world_scaling_factor translates from unitless coordinates to the global coordinate unit which is cm.
    // Given that the incoming model from Onshape has values in meters, to translate those to unitless coordinates
    // we multiply the incoming coordinates by 100 to turn them into cm, and then we divide by the world_scaling_factor.
    factor = 100 / uil_get_world_scaling_factor();

    // Let the user know whether it matches the model scale.
    if (d < 0) {
      props.body = "Cannot determine dimensions of reference model you are loading, probably due to lack of edges in the Onshape description. Are you sure you want to proceed?";
      must_bring_up_warning_window = true;
    }
    else if (d * factor < 0.01 || d * factor > 100) {
      props.body = `Your current workspace unit is ${App.state.display_units}. You are loading a reference model with dimension roughly ${get_display_unit_from_unitless_value(d * factor)} ${App.state.display_units}. Are you sure you want to proceed?`;
      must_bring_up_warning_window = true;
    }
  }
  else {
    // No world_scaling_factor is set yet, so we'll set it to be equal to 100, so that an imported onshape model in meters will
    // translate to centimeters. And we will set a scaling factor to use in import of 1/d, so that the model with its incoming
    // coordinates as meters will be scaled into a unit box in our internal unitless values.
    if (d < 0) d = 1;
        uil_set_world_scaling_factor(100*d);
    factor = 1/d;
  }

  const proceed = (
    !must_bring_up_warning_window
    || await show_boolean_dialog(props)
  );

  if (proceed)
    uid_proceed_with_loading_onshape_partstudio_body_details_model(
        factor,
        as_reference,
        false,
        body_details_call1_response,
        body_details_call2_response,
        body_details_call3_response,
    );
}

export function get_onshape_imported_model_bounding_box(json_obj_bd) {
    const max = optimum(gt);
    return pipe(
        json_obj_bd.bodies,
        map(b => {
            if (b.edges.length > 0)
                return calculate_edge_bounding_box(b.edges);
            else if (b.faces.length > 0 && b.faces.some(is_torus))
                return max(b.faces.filter(is_torus).map(calculate_torus_bounding_box));
            else
                return undefined;
        }),
        max,
        x => x === undefined ? -1 : x.toFixed(3),
    );
}

function calculate_edge_bounding_box(edges) {
    // Returns bounding box in default tesselation coordinate units, which are METERS in Onshape.
    let minx = Infinity;
    let miny = Infinity;
    let minz = Infinity;
    let maxx = -Infinity;
    let maxy = -Infinity;
    let maxz = -Infinity;
    //console.log("In get_onshape_imported_model_bounding_box.");
    for (const e of edges) {
        const pstart = new THREE.Vector3();
        const pend = new THREE.Vector3();
        pstart.x = (typeof e.geometry.startPoint.x !== 'undefined')
            ? e.geometry.startPoint.x
            : e.geometry.startPoint[0];
        pstart.y = (typeof e.geometry.startPoint.y !== 'undefined')
            ? e.geometry.startPoint.y
            : e.geometry.startPoint[1];
        pstart.z = (typeof e.geometry.startPoint.z !== 'undefined')
            ? e.geometry.startPoint.z
            : e.geometry.startPoint[2];
        pend.x = (typeof e.geometry.endPoint.x !== 'undefined')
            ? e.geometry.endPoint.x
            : e.geometry.endPoint[0];
        pend.y = (typeof e.geometry.endPoint.y !== 'undefined')
            ? e.geometry.endPoint.y
            : e.geometry.endPoint[1];
        pend.z = (typeof e.geometry.endPoint.z !== 'undefined')
            ? e.geometry.endPoint.z
            : e.geometry.endPoint[2];

        if (pstart.x < minx)
            minx = pstart.x;
        if (pstart.y < miny)
            miny = pstart.y;
        if (pstart.z < minz)
            minz = pstart.z;
        if (pstart.x > maxx)
            maxx = pstart.x;
        if (pstart.y > maxy)
            maxy = pstart.y;
        if (pstart.z > maxz)
            maxz = pstart.z;

        if (pend.x < minx)
            minx = pend.x;
        if (pend.y < miny)
            miny = pend.y;
        if (pend.z < minz)
            minz = pend.z;
        if (pend.x > maxx)
            maxx = pend.x;
        if (pend.y > maxy)
            maxy = pend.y;
        if (pend.z > maxz)
            maxz = pend.z;
    }
    return Math.max(
        maxx - minx,
        maxy - miny,
        maxz - minz,
    );
}

function is_torus(face) {
    return face?.surface?.type === 'torus';
}

function calculate_torus_bounding_box(face) {
    return Math.max(
        onshape_corner_diff(face.box, 0),
        onshape_corner_diff(face.box, 1),
        onshape_corner_diff(face.box, 2),
    );
}

function onshape_corner_diff(box, i) {
    return box.maxCorner[i] - box.minCorner[i];
}

export const uid_proceed_with_loading_onshape_partstudio_body_details_model = withSpinner('Importing body details', async function uid_proceed_with_loading_onshape_partstudio_body_details_model(
    factor,
    as_reference,
    is_debug_call = false,
    body_details_call1_response,
    body_details_call2_response,
    body_details_call3_response,
) {
  const error_message_strings = [];
  suppress_ui_messages();

  API.log('Load Onshape PartStudio into Phi', as_reference ? "As reference" : "Convert to Phi");

  if (!is_debug_call && typeof as_reference == 'undefined') {
    // We've been called from the modal, need to read the actual value of as_reference
    as_reference = document.getElementById("proceedWithImportModal_as_reference").value == 1 ? true : false;
    factor = document.getElementById("proceedWithImportModal_factor").value;
  }

  // Hide the modal and show the spinner
  hide_modal("proceedWithImportModal");
  await next_frame();

  var body_details = body_details_call1_response;
  var tessellated_edges = body_details_call2_response;
  var tessellated_faces = body_details_call3_response;

  var tmp_reference_vertices = [];
  var tmp_reference_edges = [];
  var tmp_reference_faces = [];
  var tmp_reference_bodies = [];

  uid_reference_vertex_coords_hash.clear();

  console_log_xml("BODY DETAILS:");
  console_log_xml(JSON.stringify(body_details));
  console_log_xml("TESSELATED EDGES:");
  console_log_xml(JSON.stringify(tessellated_edges));
  console_log_xml("TESSELATED FACES:");
  console_log_xml(JSON.stringify(tessellated_faces));

  //Always display reference vertices XXX
  do_not_load_reference_vertices = false;

  var onshape_id_to_reference_object = new HashTable();
  var reference_face_edges_onshape_ids = new HashTable();
  var reference_face_normal_onshape_ids = new HashTable();
  var reference_face_orientation_onshape_ids = new HashTable();
  var reference_edge_vertices_onshape_ids = new HashTable();

  const json_obj_bd = body_details;
  const json_tess_faces = tessellated_faces;
  const json_tess_edges = tessellated_edges;

  var tessellated_faces_body_array;
  if (json_tess_faces && json_tess_faces.bodies)
    tessellated_faces_body_array = json_tess_faces.bodies;
  else if (json_tess_faces)
    tessellated_faces_body_array = json_tess_faces;
  else {
    // Error, bad tessellation information
    error_message_strings.push("Bad face tessellation information received");
    tessellated_faces_body_array = [];
  }

  process_onshape_input_before_loading_to_phi(
    json_obj_bd,
    json_tess_faces,
    json_tess_edges,
    factor,
    as_reference,
    onshape_id_to_reference_object,
    reference_face_edges_onshape_ids,
    reference_face_normal_onshape_ids,
    reference_face_orientation_onshape_ids,
    reference_edge_vertices_onshape_ids,
    tmp_reference_vertices,
    tmp_reference_edges,
    tmp_reference_faces,
    tmp_reference_bodies,
    tessellated_faces_body_array,
    error_message_strings,
  );

  if (as_reference) {
    resize_all_reference_vertices_for_zoom(0, viewSize, 0);
    var bb = uid_calculate_global_bounding_box(true, false, false);
    uid_ball_size = uid_default_ball_size * bb.dimension;

    App.state.showing_reference_partstudio_models = true;

    uid_reference_partstudio_raynode.add(uid_ref_partstudio_faces_raynode);
    uid_reference_partstudio_raynode.add(uid_ref_partstudio_edges_raynode);
    uid_reference_partstudio_raynode.add(uid_ref_partstudio_edges_visible_raynode);

    request_setting_button_states();
    view_all(false);
    animate();
  }
  else {
    const res = await load_body_details_as_phi(
        tmp_reference_bodies,
        onshape_id_to_reference_object,
        tessellated_faces_body_array,
        error_message_strings
    );
  }

  unsuppress_ui_messages_if_ready();

  /* We have already informed the user of this, no need to be pedantic
  const d = get_display_unit_from_unitless_value(get_onshape_imported_model_bounding_box(json_obj_bd) * factor);
  if (d <= 0)
    error_message_strings.push("Imported model bounding box could not be determined.");
  */

  // + error_message_strings.join('\n')
  if (error_message_strings.length > 0)
    display_message_to_user(
        LOG.WARN,
        'This model was not successfully imported. Click on this message for more information.',
        true,
        () => show_import_troubleshooting_modal(error_message_strings),
    );
});

async function load_body_details_as_phi(
    tmp_reference_bodies,
    onshape_id_to_reference_object,
    tessellated_faces_body_array,
    error_message_strings
) {

  const all_error_messages = [];
  for (const bid of tmp_reference_bodies) {
      const current_error_messages = [];
      // Assemble elements of current body in separate arrays
      const my_entries = uid_reference_faces.entries()
          .filter(([fid, face]) => face.onshape_body_id === bid);

      const body_reference_faces = my_entries.map(x => x[0]);
      const body_reference_edges = unique(my_entries.flatMap(([fid, face]) => face.edges));
      const body_reference_vertices = unique(
          body_reference_edges.flatMap(eid => uid_reference_edges.getItem(eid).vertices)
      )

      // This is just to keep the signature of the functions we'll be calling.
      const body_reference_bodies = [bid];

      const res = load_reference_objects_to_phi(
          body_reference_vertices,
          body_reference_edges,
          body_reference_faces,
          body_reference_bodies,
          false,
          current_error_messages,
      );

      if (current_error_messages.length === 0) continue;

      all_error_messages.push(... current_error_messages);

      /* If we failed to load the phi model, try a different approach.
        * Select all model faces and try to convert them to phi faces one by one.
        */
      const failed_faces = await attempt_converting_faces_one_by_one(
          body_reference_faces,
          onshape_id_to_reference_object,
          tessellated_faces_body_array,
      );
      if (failed_faces.length > 0)
          error_message_strings.push(
              `Failed to load these ${failed_faces.length} faces:`
              + failed_faces
                  .map(x => onshape_id_to_reference_object.getItem(x).onshape_id)
                  .join(', ')
          );
    };

    view_all(false);

    // If we've still not loaded anything, show the reference model.
    if (uid_vertices.length == 0)
        show_reference_partstudio_model();

    if (all_error_messages.length > 0) {
        error_message_strings.push(... all_error_messages);
        error_message_strings.push('Failed to convert the entire model. We attempted to convert as many faces as possible independently.');
    }

    handle_missing_tangencies();
    set_button_states();
}

async function attempt_converting_faces_one_by_one(
    body_reference_faces,
    onshape_id_to_reference_object,
    tessellated_faces_body_array,
) {
    const body_faces_onshape_ids = body_reference_faces
        .map(fid => uid_reference_faces.getItem(fid).onshape_id);

    suppress_zero_thickness_checking();
    suppress_animate();
    suppress_undo_tracking();
    const failed_faces = [];
    await withProgress('Loading reference faces', f => {
        const ref_obj = onshape_id_to_reference_object.getItem(f.id);
        var error_messages = [];
        const res = convert_single_reference_face_to_phi(ref_obj.uid_object.object3d, error_messages);

        // Animate once
        animate();

        if (!res) failed_faces.push(f.id);
    })(tessellated_faces_body_array
        .flatMap(x => x.faces)
        .filter(x => body_faces_onshape_ids.includes(x.id)));

    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    unsuppress_zero_thickness_checking_if_ready();
    return failed_faces;
}

function process_onshape_input_before_loading_to_phi(
      json_obj_bd, json_tess_faces, json_tess_edges, factor, as_reference, onshape_id_to_reference_object,
      reference_face_edges_onshape_ids, reference_face_normal_onshape_ids, reference_face_orientation_onshape_ids,
      reference_edge_vertices_onshape_ids,
      tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces, tmp_reference_bodies,
      tessellated_faces_body_array, error_message_strings) {

  // Before proceeding with the load we will replace all IDs with newly generated ones. To do this we must keep
  // an association mechanism between original Onshape IDs and newly generated ones. All IDs for bodies, face, edges
  // are found within the bodies description.
  var onshape_to_new_id_hash = new HashTable();
  json_obj_bd.bodies.forEach(function(b) {
    onshape_to_new_id_hash.setItem(b.id, create_onshape_phi_id(b.id));
    b.faces.forEach(function(f) {
      onshape_to_new_id_hash.setItem(f.id, create_onshape_phi_id(f.id));
    });
    b.edges.forEach(function(e) {
      onshape_to_new_id_hash.setItem(e.id, create_onshape_phi_id(e.id));
    });
    b.vertices.forEach(function(v) {
      onshape_to_new_id_hash.setItem(v.id, create_onshape_phi_id(v.id));
    });
  });

  // Now substitute all IDs in the input json description
  json_obj_bd.bodies.forEach(function(b) {
    b.id = onshape_to_new_id_hash.getItem(b.id);
    b.faces.forEach(function(f) {
      f.id = onshape_to_new_id_hash.getItem(f.id);
      f.loops.forEach(function(l){
        l.coedges.forEach(function(c){
          c.edgeId = onshape_to_new_id_hash.getItem(c.edgeId);
        });
      });
    });
    b.edges.forEach(function(e) {
      e.id = onshape_to_new_id_hash.getItem(e.id);
      e.vertices[0] = onshape_to_new_id_hash.getItem(e.vertices[0]);
      e.vertices[1] = onshape_to_new_id_hash.getItem(e.vertices[1]);
    });
    b.vertices.forEach(function(v) {
      v.id = onshape_to_new_id_hash.getItem(v.id);
    });
  });

  if (json_tess_faces && json_tess_faces.bodies)
    tessellated_faces_body_array = json_tess_faces.bodies;
  else if (json_tess_faces)
    tessellated_faces_body_array = json_tess_faces;
  else {
    // Error, bad tessellation information
    error_message_strings.push("Bad face tessellation information received");
    //display_message_to_user(LOG.WARN, "Bad face tessellation information received"); // IMPORTFAIL
    tessellated_faces_body_array = [];
  }

  tessellated_faces_body_array.forEach(function(b) {
    b.id = onshape_to_new_id_hash.getItem(b.id);
    b.faces.forEach(function(f) {
      f.id = onshape_to_new_id_hash.getItem(f.id)
    });
  });

  json_tess_edges.forEach(function(b) {
    b.id = onshape_to_new_id_hash.getItem(b.id);
    b.edges.forEach(function(e) {
      e.id = onshape_to_new_id_hash.getItem(e.id)
    });
  });

  // Create an array with IDs of all tessellated faces
  var all_tessellated_face_ids = [];
  tessellated_faces_body_array.forEach(function(b) {
    b.faces.forEach(function(f) {
      all_tessellated_face_ids.push(f.id);
    });
  });

  json_obj_bd.bodies.forEach(function(b) {
    uid_reference_bodies.setItem(b.id, get_global_id());
    tmp_reference_bodies.push(b.id);
    b.faces.forEach(function(f) {
      //console_log("Adding face:", f.id);
      var face_edge_array = new Array();
      f.loops.forEach(function(l) {
        l.coedges.forEach(function(e) {
          //console_log("  Adding face edge: ", e.edgeId);
          face_edge_array.push(e.edgeId);
        });
        reference_face_edges_onshape_ids.setItem(f.id, face_edge_array);
      });
      if (f.surface.normal) {
        reference_face_normal_onshape_ids.setItem(f.id, new THREE.Vector3(f.surface.normal[0], f.surface.normal[1], f.surface.normal[2]));
        reference_face_orientation_onshape_ids.setItem(f.id, f.orientation);
      }

      // Also create temporarily a reference object if it doesn't exist in tessellated faces
      if (all_tessellated_face_ids.indexOf(f.id) == -1) {
        var ref_face_obj = new Uid_ref_face( f.id, [], [],
                  reference_face_normal_onshape_ids.getItem(f.id),
                  reference_face_orientation_onshape_ids.getItem(f.id),
                  b.id, "partstudio" );
        var id = uid_add_reference_face(ref_face_obj);
        tmp_reference_faces.push(id);
        onshape_id_to_reference_object.setItem(ref_face_obj.onshape_id, ref_face_obj);
        console.log("Found reference face with ID " + f.id + " that exists in body_details, but not in tessellated_faces.");
      }
    });

    // Add all edge endpoints as reference vertices
    b.edges.forEach(function(e) {
      // Note: In some cases, e.g. for an edge that is a circle, onshape doesn't have corresponding vertices. But the edge geometry does have
      // a startpoint and endpoint. We use the start/end points to create our own reference vertices. In these cases we generate random ids
      // for these vertices.
      //console_log("New edge:");

      var refv1 = new THREE.Vector3();
      var refv2 = new THREE.Vector3();
      refv1.x = (typeof e.geometry.startPoint.x !== 'undefined') ? e.geometry.startPoint.x : e.geometry.startPoint[0];
      refv1.y = (typeof e.geometry.startPoint.y !== 'undefined') ? e.geometry.startPoint.y : e.geometry.startPoint[1];
      refv1.z = (typeof e.geometry.startPoint.z !== 'undefined') ? e.geometry.startPoint.z : e.geometry.startPoint[2];
      refv2.x = (typeof e.geometry.endPoint.x !== 'undefined') ? e.geometry.endPoint.x : e.geometry.endPoint[0];
      refv2.y = (typeof e.geometry.endPoint.y !== 'undefined') ? e.geometry.endPoint.y : e.geometry.endPoint[1];
      refv2.z = (typeof e.geometry.endPoint.z !== 'undefined') ? e.geometry.endPoint.z : e.geometry.endPoint[2];
      refv1.multiplyScalar(factor);
      refv2.multiplyScalar(factor);

      var edge_vertex_array = new Array();

      // Check if we have already added a reference vertex with the same Onshape ID, in which case we skip this. First for the 1st vertex of this edge
      var found = false;
      uid_reference_vertices.values().forEach(function(existing_ref_vertex){
        if (existing_ref_vertex.onshape_id == e.vertices[0])
          found = true;
      });
      if (!found) {
        var onshape_id = e.vertices[0] || e.id + "_1";
        var ref_vertex_obj1 = new Uid_ref_vertex(onshape_id, refv1, b.id, "partstudio");
        var str = Math.round(refv1.x * 1000000).toString() + Math.round(refv1.y * 1000000).toString() + Math.round(refv1.z * 1000000).toString();
        var id = uid_add_reference_vertex(ref_vertex_obj1, uid_reference_vertex_coords_hash.hasItem(str));
        tmp_reference_vertices.push(id);

        onshape_id_to_reference_object.setItem(ref_vertex_obj1.onshape_id, ref_vertex_obj1);
        edge_vertex_array.push(onshape_id);
        console_log("Adding vertex with Onshape Id:", e.vertices[0]);
      }
      else {
        edge_vertex_array.push(e.vertices[0]);
        console_log("Skipping vertex with Onshape Id:", e.vertices[0]);
      }

      // Check if we have already added a reference vertex with the same Onshape ID, in which case we skip this. Then for the 2nd vertex of this edge
      var found = false;
      uid_reference_vertices.values().forEach(function(existing_ref_vertex){
        if (existing_ref_vertex.onshape_id == e.vertices[1])
          found = true;
      });
      if (!found) {
        onshape_id = e.vertices[1] || e.id + "_2";
        var ref_vertex_obj2 = new Uid_ref_vertex(onshape_id, refv2, b.id, "partstudio");
        var str = Math.round(refv2.x * 1000000).toString() + Math.round(refv2.y * 1000000).toString() + Math.round(refv2.z * 1000000).toString();
        var id = uid_add_reference_vertex(ref_vertex_obj2, uid_reference_vertex_coords_hash.hasItem(str));
        tmp_reference_vertices.push(id);

        onshape_id_to_reference_object.setItem(ref_vertex_obj2.onshape_id, ref_vertex_obj2);
        edge_vertex_array.push(onshape_id);
        console_log("Adding vertex with Onshape Id:", e.vertices[1]);
      }
      else {
        edge_vertex_array.push(e.vertices[1]);
        console_log("Skipping vertex with Onshape Id:", e.vertices[1]);
      }

      reference_edge_vertices_onshape_ids.setItem(e.id, edge_vertex_array);

      var ref_edge_obj = new Uid_ref_edge(e.id, b.id, "partstudio");

      var v0 = new THREE.Vector3();
      var v1 = new THREE.Vector3();
      v0.x = (typeof e.geometry.startVector.x !== 'undefined') ? e.geometry.startVector.x : e.geometry.startVector[0];
      v0.y = (typeof e.geometry.startVector.y !== 'undefined') ? e.geometry.startVector.y : e.geometry.startVector[1];
      v0.z = (typeof e.geometry.startVector.z !== 'undefined') ? e.geometry.startVector.z : e.geometry.startVector[2];
      v1.x = (typeof e.geometry.endVector.x !== 'undefined') ? e.geometry.endVector.x : e.geometry.endVector[0];
      v1.y = (typeof e.geometry.endVector.y !== 'undefined') ? e.geometry.endVector.y : e.geometry.endVector[1];
      v1.z = (typeof e.geometry.endVector.z !== 'undefined') ? e.geometry.endVector.z : e.geometry.endVector[2];
      ref_edge_obj.set_v0(v0);
      ref_edge_obj.set_v1(v1);

      onshape_id_to_reference_object.setItem(e.id, ref_edge_obj);

      // Also add the Onshape data within the reference edge structure
      var onshape_edge_data_json_string = null;
      json_obj_bd.bodies.forEach(function(ob) {
        ob.edges.forEach(function(oe) {
          if (oe.id == e.id) {
            onshape_edge_data_json_string = JSON.stringify(oe);
          }
        });
      });
      ref_edge_obj.set_onshape_edge_data_json_string(onshape_edge_data_json_string);

    });
  });

  tessellated_faces_body_array.forEach(function(b) {
    b.faces.forEach(function(f) {
      //console_log(f.id);
      //console.log("IMPORTED FACE DATA", f);
      if (!reference_face_edges_onshape_ids.hasItem(f.id))
        console_log_xml("Unexpected face");

      // Array of reference vertices
      var reference_vertices = [];
      var reference_triangles = [];
      var reference_vertices_hash = new HashTable();

      f.facets.forEach(function(c) {
        // For each of the 3 vertices for this facet
        c.vertices.forEach(function(v) {
          // Extract three new vertex
          var refv = new THREE.Vector3();
          refv.x = (typeof v.x !== 'undefined') ? v.x : v[0];
          refv.y = (typeof v.y !== 'undefined') ? v.y : v[1];
          refv.z = (typeof v.z !== 'undefined') ? v.z : v[2];
          refv.multiplyScalar(factor);
          var refvObj = new Uid_ref_face_vertex(0, refv);

          // Add vertex to hash
          var refvkey = refv.x + ";" + refv.y + ";" + refv.z;
          if (!reference_vertices_hash.hasItem(refvkey))
            reference_vertices_hash.setItem(refvkey, refvObj);
        });

        // Add a new triangle
        var refv1 = new THREE.Vector3();
        var refv2 = new THREE.Vector3();
        var refv3 = new THREE.Vector3();
        refv1.x = (typeof c.vertices[0].x !== 'undefined') ? c.vertices[0].x : c.vertices[0][0];
        refv1.y = (typeof c.vertices[0].y !== 'undefined') ? c.vertices[0].y : c.vertices[0][1];
        refv1.z = (typeof c.vertices[0].z !== 'undefined') ? c.vertices[0].z : c.vertices[0][2];
        refv2.x = (typeof c.vertices[1].x !== 'undefined') ? c.vertices[1].x : c.vertices[1][0];
        refv2.y = (typeof c.vertices[1].y !== 'undefined') ? c.vertices[1].y : c.vertices[1][1];
        refv2.z = (typeof c.vertices[1].z !== 'undefined') ? c.vertices[1].z : c.vertices[1][2];
        refv3.x = (typeof c.vertices[2].x !== 'undefined') ? c.vertices[2].x : c.vertices[2][0];
        refv3.y = (typeof c.vertices[2].y !== 'undefined') ? c.vertices[2].y : c.vertices[2][1];
        refv3.z = (typeof c.vertices[2].z !== 'undefined') ? c.vertices[2].z : c.vertices[2][2];
        refv1.multiplyScalar(factor);
        refv2.multiplyScalar(factor);
        refv3.multiplyScalar(factor);
        reference_triangles.push( new Uid_ref_face_triangle(0, 0, 0, refv1, refv2, refv3) );
      });

      // Now go through reference_vertices_hash and puth them in an array and also add array index in hash elements.
      reference_vertices_hash.values().forEach(function(v) {
        reference_vertices.push(v.vertex);
        var i = reference_vertices.length - 1;
        v.vindex = i;
      });

      // Now go through triangles and replace coordinates with vertex indices
      reference_triangles.forEach(function(t) {
        var refvkey = t.vertex1.x + ";" + t.vertex1.y + ";" + t.vertex1.z;
        var v = reference_vertices_hash.getItem(refvkey);
        t.index1 = v.vindex;

        var refvkey = t.vertex2.x + ";" + t.vertex2.y + ";" + t.vertex2.z;
        var v = reference_vertices_hash.getItem(refvkey);
        t.index2 = v.vindex;

        var refvkey = t.vertex3.x + ";" + t.vertex3.y + ";" + t.vertex3.z;
        var v = reference_vertices_hash.getItem(refvkey);
        t.index3 = v.vindex;
      });

      // Now add the surface
      var ref_face_obj = new Uid_ref_face( f.id, reference_vertices, reference_triangles,
              reference_face_normal_onshape_ids.getItem(f.id),
              reference_face_orientation_onshape_ids.getItem(f.id),
              b.id, "partstudio" );
      var id = uid_add_reference_face(ref_face_obj);
      tmp_reference_faces.push(id);

      onshape_id_to_reference_object.setItem(ref_face_obj.onshape_id, ref_face_obj);
    });
  });

  // Add reference edges
  json_tess_edges.forEach(function(b) {
    b.edges.forEach(function(e) {
      //console_log("-> Adding a reference edge", e.id);
      var edge_points_array = [];
      e.vertices.forEach(function(v) {
        var ev = new THREE.Vector3();
        ev.x = (typeof v.x !== 'undefined') ? v.x : v[0];
        ev.y = (typeof v.y !== 'undefined') ? v.y : v[1];
        ev.z = (typeof v.z !== 'undefined') ? v.z : v[2];
        ev.multiplyScalar(factor);
        edge_points_array.push(ev);
      });
      var ref_edge_obj = onshape_id_to_reference_object.getItem(e.id);

      ref_edge_obj.set_edge_points_array(edge_points_array);

      var id = uid_add_reference_edge(ref_edge_obj);
      tmp_reference_edges.push(id);
    });
  });

  // Complete reference object descriptions with lists of adjacent objects
  var found_incomplete_reference_object = false;

  reference_edge_vertices_onshape_ids.keys().forEach(function(e) {
    var ref_edge = onshape_id_to_reference_object.getItem(e);
    var x = reference_edge_vertices_onshape_ids.getItem(e);
    reference_edge_vertices_onshape_ids.getItem(e).forEach(function(v){
      var ref_vertex = onshape_id_to_reference_object.getItem(v);
        if (ref_edge.uid_object)
          ref_vertex.add_edge(ref_edge.uid_object.id);
        else
          found_incomplete_reference_object = true;
        if (ref_vertex.uid_object)
          ref_edge.add_vertex(ref_vertex.uid_object.id);
        else
          found_incomplete_reference_object = true;
      });
  });

  // Check all edge points arrays, that they correctly go from vertex1 to vertex2
  uid_reference_edges.values().forEach(function(re){
    var vertex1 = uid_reference_vertices.getItem(re.vertices[0]).vertex;
    var vertex2 = uid_reference_vertices.getItem(re.vertices[1]).vertex;
    if (point_point_distance(re.points[0], vertex1) > point_point_distance(re.points[0], vertex2))
      re.reverse_edge_points_array();
  });

  reference_face_edges_onshape_ids.keys().forEach(function(f){
    var ref_face = onshape_id_to_reference_object.getItem(f);
    var x = reference_face_edges_onshape_ids.getItem(f);
    reference_face_edges_onshape_ids.getItem(f).forEach(function(e) {
      var ref_edge = onshape_id_to_reference_object.getItem(e);
      if (ref_edge.uid_object)
        ref_face.add_edge(ref_edge.uid_object.id);
      else
        found_incomplete_reference_object = true;
      if (ref_face.uid_object)
        ref_edge.add_face(ref_face.uid_object.id);
      else
        found_incomplete_reference_object = true;
    });
  });

  if (found_incomplete_reference_object) {
    error_message_strings.push("Imported Onshape object descriptions were found to be incomplete. Reverted to importing as reference.");
    //display_message_to_user(LOG.WARN, "Imported Onshape object descriptions are incomplete. Reverting to importing as reference."); // IMPORTFAIL
    as_reference = true;
  }
}



export function create_onshape_phi_id(id) {
  var phi_id = get_global_id();
  return id + "_phi_" + phi_id;
}

export async function load_reference_objects_to_phi(
  tmp_reference_vertices,
  tmp_reference_edges,
  tmp_reference_faces,
  tmp_reference_bodies,
  force_everything_in_single_body,
  error_message_strings
) {

  // The ID of the inward and outward facing face so we can subsequently pop-it-out.
  var inward_looking_converted_face_label = new WrappedValue();
  inward_looking_converted_face_label.v = "";
  var outward_looking_converted_face_label = new WrappedValue();
  outward_looking_converted_face_label.v = "";

  var phi_model_string = generate_phi_model_from_onshape_part_studio_details(
    tmp_reference_vertices,
    tmp_reference_edges,
    tmp_reference_faces,
    tmp_reference_bodies,
    force_everything_in_single_body,
    inward_looking_converted_face_label,
    outward_looking_converted_face_label,
    error_message_strings,
  );
  if (!phi_model_string) {
    return error_message_strings;
  }
  window.loading_into_current_model = true;

  // Load the model
  try {
    uil_set_add_edge_pair_tangencies_on_load(true);
    await uid_load_model(phi_model_string, false, false, false, false, false);
    if (force_everything_in_single_body) {
      // Do one more thing, that is hacky, but we're in hacky mode now anyway so here goes. For the two faces added, set their sidedness to 1,
      // to make sure that we see the one from the outside and the other from the inside.
      inward_looking_converted_face_id = 0;
      outward_looking_converted_face_id = 0;
      uid_faces.keys().forEach(function(fid){
        var l = uil_get_face_label(fid);
        if (l == inward_looking_converted_face_label.v)
          inward_looking_converted_face_id = fid;
        if (l == outward_looking_converted_face_label.v)
          outward_looking_converted_face_id = fid;
      });
      show_reference_partstudio_model();
    } else {
      show_reference_partstudio_model();
    }
    uil_set_add_edge_pair_tangencies_on_load(false);
    request_setting_button_states();
    animate();
    return error_message_strings;
  } catch (error) {
    console_log_xml("FATAL ERROR");
    error_message_strings.push("Fatal error while loading reference model");
    return error_message_strings;
  }
};

function split_imported_edge_in_two(eid, tmp_reference_bodies, tmp_reference_faces, tmp_reference_edges, tmp_reference_vertices) {
  var e = uid_reference_edges.getItem(eid);

  // Check if the existing edge samples go from the first to the second vertex, or in the opposite direction
  var samples_go_from_v0_to_v1 = true;
  var eV0 = uid_reference_vertices.getItem(e.vertices[0]);
  var eV1 = uid_reference_vertices.getItem(e.vertices[1]);
  if (eV0.uid_object.object3d.position.distanceTo(e.points[0]) > eV0.uid_object.object3d.position.distanceTo(e.points[e.points.length-1]))
    samples_go_from_v0_to_v1 = false;

  // Create new vertex and two new edges
  var n = e.points.length;
  var vcoords = new THREE.Vector3();
  var arr1 = []; // This will contain the half of the edge samples that go from v0 to middle
  var arr2 = []; // This will contain the half of the edge samples that go from middle to v1
  var vtan1 = new THREE.Vector3(); // The tangent vector corresponding to vertex v0
  var vtan2 = new THREE.Vector3(); // The tangent vector corresponding to vertex v1
  var i;
  if (n%2 == 0) {
    vcoords.addVectors(e.points[n/2-1], e.points[n/2]).multiplyScalar(0.5);
    for (i = 0; i <= n/2-1; i++)
      arr1.push(e.points[i])
    arr1.push(vcoords);
    arr2.push(vcoords);
    for (i = n/2; i < n; i++)
      arr2.push(e.points[i])
    arr2.reverse();
  }
  else {
    vcoords.copy(e.points[Math.floor(n/2)]);
    for (i = 0; i < Math.floor(n/2); i++)
      arr1.push(e.points[i])
    arr1.push(vcoords);
    arr2.push(vcoords);
    for (i = Math.floor(n/2)+1; i < n; i++)
      arr2.push(e.points[i])
    arr2.reverse();
  }

  if (samples_go_from_v0_to_v1) {
    vtan1.copy(e.v0);
    vtan2.copy(e.v1);
  }
  else {
    // Flip the sample arrays
    var tmparr = [];
    tmparr = [...arr1];
    arr1 = [...arr2];
    arr2 = [...tmparr];
    arr1.reverse();
    arr2.reverse();
    vtan1.copy(e.v1);
    vtan2.copy(e.v0);
  }

  var vObj = new Uid_ref_vertex(e.onshape_id + "newV", vcoords, e.onshape_body_id);
  var str = Math.round(vcoords.x * 1000000).toString() + Math.round(vcoords.y * 1000000).toString() + Math.round(vcoords.z * 1000000).toString();
  var vid = uid_add_reference_vertex(vObj, uid_reference_vertex_coords_hash.hasItem(str));

  var e1Obj = new Uid_ref_edge(e.onshape_id + "newE1", e.onshape_body_id);
  e1Obj.set_edge_points_array(arr1);
  var ide1 = uid_add_reference_edge(e1Obj);

  var e2Obj = new Uid_ref_edge(e.onshape_id + "newE2", e.onshape_body_id);
  e2Obj.set_edge_points_array(arr2);
  var ide2 = uid_add_reference_edge(e2Obj);

  // We must check in which order these edges must be added around the face.
  // Find the face f and the list of edges f.edges
  var f0 = uid_reference_faces.getItem(e.faces[0]);
  var f1 = uid_reference_faces.getItem(e.faces[1]);

  var eind0 = f0.edges.indexOf(eid);
  var ef0prev = eind0 == 0 ? f0.edges[f0.edges.length-1] : f0.edges[eind0-1];
  var ef0nextindex = eind0 == f0.edges.length-1 ? f0.edges[0] : f0.edges[eind0+1];
  var ef0next = uid_reference_edges.getItem(ef0nextindex);
  var f0flip = false;
  if (ef0next.vertices[0] == e.vertices[0] || ef0next.vertices[1] == e.vertices[0])
    f0flip = true;

  if (f1) {
    var eind1 = f1.edges.indexOf(eid);
    var ef1prev = eind1 == 0 ? f1.edges[f1.edges.length-1] : f1.edges[eind1-1];
    var ef1nextindex = eind1 == f1.edges.length-1 ? f1.edges[0] : f1.edges[eind1+1];
    var ef1next = uid_reference_edges.getItem(ef1nextindex);
    var f1flip = false;
    if (ef1next.vertices[0] == e.vertices[0] || ef1next.vertices[1] == e.vertices[0])
      f1flip = true;
  }

  vObj.add_edge(ide1);
  vObj.add_edge(ide2);

  e1Obj.add_vertex(e.vertices[0]);
  e1Obj.add_vertex(vid);
  e2Obj.add_vertex(e.vertices[1]);
  e2Obj.add_vertex(vid);
  e1Obj.add_face(e.faces[0]);
  e1Obj.add_face(e.faces[1]);
  e2Obj.add_face(e.faces[0]);
  e2Obj.add_face(e.faces[1]);

  // We now replace the old edge IDs with the new (after the split) edge IDs for the vertices on either side of the
  // old edge. But we have to be careful and special-case the case where originally we had only one edge, with the
  // same vertex on both ends.

  if (e.vertices[0] != e.vertices[1]) {
    var v0 = uid_reference_vertices.getItem(e.vertices[0]);
    for (i = 0; i < v0.edges.length; i++) {
      if (v0.edges[i] == e.uid_object.id)
        v0.edges[i] = e1Obj.uid_object.id;
    }
    var v1 = uid_reference_vertices.getItem(e.vertices[1]);
    for (i = 0; i < v1.edges.length; i++) {
      if (v1.edges[i] == e.uid_object.id)
        v1.edges[i] = e2Obj.uid_object.id;
    }
  }
  else {
    var v0 = uid_reference_vertices.getItem(e.vertices[0]);

    for (i = v0.edges.length - 1 ; i >= 0; i--) {
      if (v0.edges[i] == e.uid_object.id) {
        // Insert one of the two new edges at the index of the old edge, removing the old edge entry,
        // then insert the other new edge at the next index, without removing anything.
        // So for example from [1, 2, old, 4, 5] it will become [1, 2, new1, new2, 4, 5]
        v0.edges.splice(i, 1, e1Obj.uid_object.id);
        v0.edges.splice(i+1, 0, e2Obj.uid_object.id);
      }
    }
  }

  e1Obj.set_v0(vtan1);
  e2Obj.set_v0(vtan2.multiplyScalar(-1));
  var vtmp1 = new THREE.Vector3();
  vtmp1.subVectors(e1Obj.points[e1Obj.points.length-2], e1Obj.points[e1Obj.points.length-1]).normalize();
  e1Obj.set_v1(vtmp1.multiplyScalar(-1));
  vtmp1.subVectors(e2Obj.points[e2Obj.points.length-2], e2Obj.points[e2Obj.points.length-1]).normalize();
  e2Obj.set_v1(vtmp1.multiplyScalar(-1));

  var f = uid_reference_faces.getItem(e.faces[0]);
  var tmpedgearray = [];
  f.edges.forEach(function(eid) {
    if (eid == e.uid_object.id) {
      if (f0flip) {
        tmpedgearray.push(e2Obj.uid_object.id);
        tmpedgearray.push(e1Obj.uid_object.id);
      }
      else {
        tmpedgearray.push(e1Obj.uid_object.id);
        tmpedgearray.push(e2Obj.uid_object.id);
      }
    }
    else {
      tmpedgearray.push(eid);
    }
  });
  f.edges = [];
  tmpedgearray.forEach(function(eid){
    f.add_edge(eid);
  });

  f = uid_reference_faces.getItem(e.faces[1]);
  if (f) {
    tmpedgearray = [];
    f.edges.forEach(function(eid) {
      if (eid == e.uid_object.id) {
        if (f1flip) {
          tmpedgearray.push(e2Obj.uid_object.id);
          tmpedgearray.push(e1Obj.uid_object.id);
        }
        else {
          tmpedgearray.push(e1Obj.uid_object.id);
          tmpedgearray.push(e2Obj.uid_object.id);
        }
      }
      else {
        tmpedgearray.push(eid);
      }
    });
    f.edges = [];
    tmpedgearray.forEach(function(eid){
      f.add_edge(eid);
    });
  }

  // Remove old edge from list of reference edges
  uid_reference_edges.removeItem(eid);

  // Remove old edge from tmp_reference_edges
  tmp_reference_edges.splice(tmp_reference_edges.indexOf(eid),1);

  // Add new edges into tmp_reference_edges
  tmp_reference_edges.push(ide1);
  tmp_reference_edges.push(ide2);

  // Add new vertex into tmp_reference_vertices
  tmp_reference_vertices.push(vid);
}

export function generate_phi_model_from_onshape_part_studio_details(tmp_reference_vertices, tmp_reference_edges,
              tmp_reference_faces, tmp_reference_bodies, force_everything_in_single_body,
              inward_looking_converted_face_label, outward_looking_converted_face_label,
              error_message_strings) {

  // IMPORTANT NOTE:
  // In its regular use, this function will parse the reference objects lattice and attempt to generate an equivalent phi description.
  // There is one special case, however, which occurs when "force_everything_in_single_body" is true. In this case we are trying to
  // "mock" a body from a single surface. To do this we take care of 2 things:
  // (a) We ignore the bodies in the lattice structure and place everything under a single body
  // (b) We receive the same single face twice. We have to take care of and handle specially the second face. In particular:
  //  - We assign a new ID to it.
  //  - We flip the order of edges around it w.r.t. what it would normally be.
  // Before starting run some checks to make sure the model being imported is according to Phi specs.
  var model_ok = true;
  var error_message = "";
  var try_splitting_imported_edges_if_necessary = true;

  // Correct the face normals based on the orientation flag
  uid_reference_faces.values().forEach(function(f) {
    if (f.normal.length() > 0 && !f.orientation) {
      f.normal.multiplyScalar(-1);
      f.orientation = true;
    }
  });

  // Squash double vertices
  squash_imported_vertices(tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces);

  // Make sure all faces have at least 3 edges by splitting current edges if necessary
  var faces_that_are_attached_to_edges_that_will_be_split = [];
  var faces_that_had_less_than_3_edges = [];
  var faces_with_less_than_3_edges_found = false;
  tmp_reference_faces.forEach(function(rf_id) {
    var rf = uid_reference_faces.getItem(rf_id);
    if (rf.edges.length < 3)
      faces_with_less_than_3_edges_found = true;
  });

  if (faces_with_less_than_3_edges_found && try_splitting_imported_edges_if_necessary) {
    split_imported_edges(tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces, tmp_reference_bodies,
      faces_that_are_attached_to_edges_that_will_be_split, faces_that_had_less_than_3_edges);

    // We need to ensure that the faces that had less than 3 edges re still facing the right way
    faces_that_had_less_than_3_edges.forEach(function(fid){
    if (reference_face_has_one_connected_edge_loop(fid) && uid_reference_faces.getItem(fid).normal.length() > 0)
      order_imported_edges_and_vertices_around_face(fid);
    });
  }
  else if (faces_with_less_than_3_edges_found) {
    model_ok = false;
    error_message = 'Faces with less than 3 edges were detected in the imported model. Phi expects all faces to have at least 3 edges. Interrupting operation.';
  }

  // Make sure all faces have their edges and vertices correctly ordered (if possible). We work one body at a time.
  // Look at all faces of the body, starting from the "most promising one", to make sure they match,
  // or flip order of those that don't match.
  // Note: Don't do this in the second attempt where we convert one face at a time (and the force_everything_in_single_body
  // flag is set), it's futile.
  if (!force_everything_in_single_body)
    tmp_reference_bodies.forEach(function(bid){
      check_order_of_all_faces_of_one_body(bid, tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces,
        faces_that_had_less_than_3_edges);
    });

  if (!model_ok) {
    error_message_strings.push(error_message);
    return null;
  }

  // Make sure all face edges form a connected loop.
  tmp_reference_faces.forEach(function(rf_id) {
    if (!reference_face_has_one_connected_edge_loop(rf_id)) {
      model_ok = false;
      error_message = 'Faces with disconnected edge loops were detected in the imported model. ' +
                      'Phi expects all edges of a face to form a single connected loop. These faces were not loaded.';
    }
  });

  if (!model_ok) {
    error_message_strings.push(error_message);
    return;
  }

  // Dump the new phi model description into a string
  var phi_string = dump_imported_model_description_to_string(tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces,
    tmp_reference_bodies, force_everything_in_single_body, inward_looking_converted_face_label,
    outward_looking_converted_face_label, error_message_strings);

  return phi_string;
}

function faces_of_reference_body(bid) {
  var fs = [];
  uid_reference_faces.keys().forEach(function(fid){
    const f = uid_reference_faces.getItem(fid);
    if (f.onshape_body_id == uid_reference_bodies.getItem(bid))
      fs.push(fid);
  });
  return fs;
}

function ordered_vertices_of_reference_face(fid) {
  var f = uid_reference_faces.getItem(fid);
  var ordered_vertices = [];

  for (var i = 0; i < f.edges.length; i++){
    var eid = f.edges[i];
    var prev_eid = (i > 0 ? f.edges[i-1] : f.edges[f.edges.length-1]);
    var v_prev1 = uid_reference_edges.getItem(prev_eid).vertices[0];
    var v_prev2 = uid_reference_edges.getItem(prev_eid).vertices[1];
    var common_vid = uid_reference_edges.getItem(eid).vertices.includes(v_prev1) ? v_prev1 : v_prev2;
    ordered_vertices.push(common_vid);
  }
  return(ordered_vertices);
}

function squash_imported_vertices(tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces) {

  // "Squash" any double vertices, i.e. two vertices at exactly the same coordinates. Substitute the one with the other
  var squash_pairs = [];
  var thresh = 1e-5;
  for (var i = 0 ; i < tmp_reference_vertices.length - 1 ; i ++ ) {
    for (var j = i + 1 ; j < tmp_reference_vertices.length ; j ++ ) {
      var vec1 = uid_reference_vertices.getItem(tmp_reference_vertices[i]).uid_object.object3d.position;
      var vec2 = uid_reference_vertices.getItem(tmp_reference_vertices[j]).uid_object.object3d.position;
      if (vec1.distanceTo(vec2) < thresh)
        squash_pairs.push([tmp_reference_vertices[i], tmp_reference_vertices[j]]);
    }
  }

  squash_pairs.forEach(function(pair){
    var tmp_v1 = pair[0];
    var tmp_v2 = pair[1];
    var v1 = uid_reference_vertices.getItem(tmp_v1);
    var v2 = uid_reference_vertices.getItem(tmp_v2);

    // Check if the two vertices are on the same (0-length) edge
    var common_redge_id = 0;
    uid_reference_edges.values().forEach(function(re) {
      if (re.vertices.includes(tmp_v1) && re.vertices.includes(tmp_v2)) {
        common_redge_id = re.id;
      }
    });

    // Get rid of tmp_v2
    tmp_reference_vertices.splice(tmp_reference_vertices.indexOf(tmp_v2), 1);
    uid_reference_vertices.removeItem(tmp_v2);

    // Take edges of v2 and add them to edges of v1 - unless it's the common edge
    v2.edges.forEach(function(eid){
      if (eid != common_redge_id)
        push_nodups(eid, v1.edges);
    });

    // Take all faces and remove the common edge
    tmp_reference_faces.forEach(function(rfid){
      var rf = uid_reference_faces.getItem(rfid);
      for (var i = rf.edges.length-1; i >= 0; i--) {
        if (rf.edges[i] == common_redge_id)
          rf.edges.splice(i,1);
      }
    });

    // Get rid of the common edge
    if (common_redge_id) {
      tmp_reference_edges.splice(tmp_reference_edges.indexOf(common_redge_id), 1);
    }

    // Take all edges and replace reference to v2 with reference to v1
    uid_reference_edges.values().forEach(function(re){
      for (var i = 0; i < re.vertices.length; i++)
        if (re.vertices[i] == tmp_v2)
          re.vertices[i] = tmp_v1;
    });
  });
}

function generate_face_normal(f) {
  // Attempts to generate a face normal when we don't have one. Kinda sketchy and "best-effort", I am not using it.

  if (f.edges.length < 3)
    return;

  var all_vertices = [];
  f.edges.forEach(function(eid){
    uid_reference_edges.getItem(eid).vertices.forEach(function(rvid){
      push_nodups(rvid, all_vertices);
    });
  });

  // Use face vertices to find center of mass
  var cofm = new THREE.Vector3(0,0,0);
  all_vertices.forEach(function(vid){
    cofm.add(uid_reference_vertices.getItem(vid).uid_object.object3d.position);
  });
  cofm.multiplyScalar(1/(all_vertices.length));

  var vA = uid_reference_edges.getItem(f.edges[0]).vertices[0];
  var vB = uid_reference_edges.getItem(f.edges[0]).vertices[1];
  var vC = uid_reference_edges.getItem(f.edges[1]).vertices[0];
  var vD = uid_reference_edges.getItem(f.edges[1]).vertices[1];

  var v1;
  var v2;
  if (vA == vC) {
    v1 = vB;
    v2 = vD;
  }
  else if (vA == vD) {
    v1 = vB;
    v2 = vC;
  }
  else if (vB == vC) {
    v1 = vA;
    v2 = vD;
  }
  else if (vB == vD) {
    v1 = vA;
    v2 = vC;
  }

  // Calculate X-product from vA to vB wrt center of mass
  var dA = new THREE.Vector3();
  var dB = new THREE.Vector3();
  var n = new THREE.Vector3();
  dA.subVectors(uid_reference_vertices.getItem(v1).uid_object.object3d.position, cofm);
  dB.subVectors(uid_reference_vertices.getItem(v2).uid_object.object3d.position, cofm);
  n.crossVectors(dA, dB);

  f.normal.copy(n.normalize());
}

function split_imported_edges(tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces, tmp_reference_bodies,
  faces_that_are_attached_to_edges_that_will_be_split, faces_that_had_less_than_3_edges) {

  var finished_splitting = false;

  while (!finished_splitting) {
    var edges_to_split = [];
    tmp_reference_faces.forEach(function(rf_id) {
      var rf = uid_reference_faces.getItem(rf_id);
      if (rf.edges.length < 3) {
        rf.edges.forEach(function(eid) {
          push_nodups(eid, edges_to_split)
          var e = uid_reference_edges.getItem(eid);
          e.faces.forEach(function(rfid){
            push_nodups(rfid, faces_that_are_attached_to_edges_that_will_be_split);
          });
        });
        push_nodups(rf_id, faces_that_had_less_than_3_edges);
      }
    });

    // Split the necessary edges
    if (edges_to_split.length > 0)
      edges_to_split.forEach(function(eid){
        split_imported_edge_in_two(eid, tmp_reference_bodies, tmp_reference_faces, tmp_reference_edges, tmp_reference_vertices);
      });
    else {
      finished_splitting = true;
    }
  }
}

function order_imported_edges_and_vertices_around_face(rfid) {
  // We assume the edges are already ordered, but we need to decide whether we need to flip them.

  var votes_good = 0;
  var votes_bad = 0;
  var f = uid_reference_faces.getItem(rfid);

  // Walk along all edges, collecting their two vertices in order
  for (var i = 0; i < f.edges.length; i++) {
    var e_id = f.edges[i];
    var e_prev_id = (i > 0 ? f.edges[i-1] : f.edges[f.edges.length-1]);
    var e = uid_reference_edges.getItem(e_id);
    var e_prev = uid_reference_edges.getItem(e_prev_id);
    var v1id = ( e.vertices.includes(e_prev.vertices[0]) ? e_prev.vertices[0] : e_prev.vertices[1] );
    var v2id = ( e.vertices[0] == v1id ? e.vertices[1] : e.vertices[0] );

    if (vertex_pair_proceeds_in_right_order_around_normal(rfid, v1id, v2id))
      votes_good++;
    else
      votes_bad++;
  }
  if (votes_bad > votes_good) {
    var reversed_edge_array = [... f.edges];
    reversed_edge_array.reverse();
    f.edges = reversed_edge_array;
  }

  /* OLD AND OBSOLETE implementation, leaving it for reference in case I need to check something. Will later remove.

  // Start with a seed edge and vertex and keep going until we run out.

  var current_face = uid_reference_faces.getItem(rfid);
  var all_edges = [... current_face.edges];
  var all_vertices = [];
  all_edges.forEach(function(eid){
    uid_reference_edges.getItem(eid).vertices.forEach(function(rvid){
      push_nodups(rvid, all_vertices);
    });
  });

  var sorted_edges = [];
  var sorted_vertices = [];
  var current_edge = all_edges[0];

  // Decide which of the two vertices of current_edge to use as the 1st vertex based on geometry and face normal
  // This will determine the direction in which we'll walk around the loop of edges.
  var n = new THREE.Vector3().copy(current_face.normal);

  if (!current_face.orientation)
    n.multiplyScalar(-1);

  var current_vertex = order_vertices_of_reference_face_around_normal(all_vertices, n,
                                        uid_reference_edges.getItem(current_edge).vertices[0],
                                        uid_reference_edges.getItem(current_edge).vertices[1]);

  while (all_edges.length > 0) {
    sorted_edges.push(current_edge);
    sorted_vertices.push(current_vertex);
    all_edges.splice(all_edges.indexOf(current_edge),1);

    var vs = uid_reference_edges.getItem(current_edge).vertices;
    var new_vertex = vs[0] == current_vertex ? vs[1] : vs[0];
    var es = uid_reference_vertices.getItem(new_vertex).edges;
    var fes = es.filter(e => all_edges.includes(e));
    var new_edge = fes[0];

    current_vertex = new_vertex;
    current_edge = new_edge;
  }
  // Replace correctly sorted array of edges for this face.
  current_face.edges = [... sorted_edges];

  // Also make sure that every reference vertex points to the correct edges, as squashed
  // vertices will probably be left with just one edge. Use edges, which are luckier.

  // First empty all their edge lists
  uid_reference_vertices.keys().forEach(function(tmprv){
    var rv = uid_reference_vertices.getItem(tmprv);
    rv.edges = [];
  });

  // Then fill them up from the edges
  uid_reference_edges.keys().forEach(function(tmpre){
    var vs = uid_reference_edges.getItem(tmpre).vertices;
    vs.forEach(function(tmprv){
      var rv = uid_reference_vertices.getItem(tmprv);
      push_nodups(tmpre, rv.edges);
    })
  });
  */

}

function check_order_of_all_faces_of_one_body(bid, tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces,
  faces_that_had_less_than_3_edges) {

  // Must make sure that the arrays of edges and vertices are in correct order.

  var processed_faces = [];
  var best_face = find_best_reference_face_in_body(bid, faces_that_had_less_than_3_edges);
  if (!best_face)
    return;

  processed_faces.push(best_face);
  var finished = false;
  while (!finished) {
    var found_unprocessed_neighbour = false;
    processed_faces.forEach(function(processed_fid) {
      if (!found_unprocessed_neighbour) {
        var neighbours_of_face = find_neighbours_of_reference_face(processed_fid);
        var unprocessed_neighbours_of_face = neighbours_of_face.filter(fid => !processed_faces.includes(fid));
        if (unprocessed_neighbours_of_face.length > 0) {
          found_unprocessed_neighbour = true;
          align_reference_face_edges_and_vertices_based_on_neighbour_face(unprocessed_neighbours_of_face[0], processed_fid,
            tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces);
          processed_faces.push(unprocessed_neighbours_of_face[0]);
        }
      }
    });
    if (!found_unprocessed_neighbour)
      finished = true;
  }
}

function find_best_reference_face_in_body(bid, faces_that_had_less_than_3_edges) {
  // Find the face that is the best candidate for using as a reference for edge/vertex ordering. It uses normal, number of
  // edges, etc. We look for a face with a non-0 normal vector, or alternatively a face with as close to 4 edges as possible
  // but definitely more than 2 edges.

  var best_face_id = 0;
  var best_face_nedges = 999;
  var found_face_with_normal = false;

  // First look for a nice, flat, unsplit face with a normal.
  faces_of_reference_body(bid).forEach(function(fid){
    var f = uid_reference_faces.getItem(fid);
    if (!found_face_with_normal && f.normal.length() > 0 && !faces_that_had_less_than_3_edges.includes(fid)) {
      found_face_with_normal = true;
      best_face_id = fid;
    }
  });

  // If we don't find such a good face, look for next best thing.
  if (!found_face_with_normal) {
    faces_of_reference_body(bid).forEach(function(fid){
    var f = uid_reference_faces.getItem(fid);
      if (f.edges.length > 2 && best_face_id == 0) {
        best_face_id = fid;
        best_face_nedges = f.edges.length;
      }
      else if (f.edges.length > 2) {
        if (Math.abs(f.edges.length - 4) < Math.abs(best_face_nedges - 4)) {
          best_face_id = fid;
          best_face_nedges = f.edges.length;
        }
      }
    });
  }

  if (best_face_id == 0)
    best_face_id = faces_of_reference_body(bid)[0];

  return best_face_id;
}

function find_neighbours_of_reference_face(fid) {
  var f = uid_reference_faces.getItem(fid);
  var neighbours = [];
  f.edges.forEach(function(eid){
    var e = uid_reference_edges.getItem(eid);
    var fs = e.faces;
    push_nodups((fs[0] == fid ? fs[1] : fs[0]), neighbours);
  });
  return neighbours;
}

function align_reference_face_edges_and_vertices_based_on_neighbour_face(face_to_align, aligned_face,
  tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces) {

  var fa = uid_reference_faces.getItem(face_to_align);
  var fb = uid_reference_faces.getItem(aligned_face);

  // Find a common edge
  var common_edges = fa.edges.filter(eid => fb.edges.includes(eid));
  var common_edge_id = common_edges[0];

  // Find previous and next edges in aligned face, and first and second vertex of common_edge.
  var ind = fb.edges.indexOf(common_edge_id);
  var e_prev_id = fb.edges[ (ind > 0 ? ind - 1 : fb.edges.length-1 ) ];
  var e_next_id = fb.edges[ (ind < fb.edges.length-1 ? ind + 1 : 0 ) ];
  var common_edge = uid_reference_edges.getItem(common_edge_id);
  var e_prev = uid_reference_edges.getItem(e_prev_id);
  var e_next = uid_reference_edges.getItem(e_next_id);
  var v1id = ( common_edge.vertices.includes(e_prev.vertices[0]) ? e_prev.vertices[0] : e_prev.vertices[1] );
  var v2id = ( common_edge.vertices.includes(e_next.vertices[0]) ? e_next.vertices[0] : e_next.vertices[1] );

  // We now know that in the face to be aligned, we must encounter, in this order: v1id, e_common_id, v2id.
  // Use this to align that face's vertices and edges.
  order_edges_and_vertices_of_reference_face_based_on_starting_set(face_to_align, v1id, v2id);
}

function order_edges_and_vertices_of_reference_face_based_on_starting_set(face_to_align, v1id, v2id) {
  // The way this works is it takes v1-common-edge-v2 coming from the reference face, and it checks if in this
  // face they are in the same or in the opposite direction. They have to be in the opposite direction.

  var direction_found = false;
  var direction_is_correct;

  var f = uid_reference_faces.getItem(face_to_align);
  var face_vertices = ordered_vertices_of_reference_face(face_to_align);

  for (var i = 0; i < face_vertices.length; i++){
    var vid = face_vertices[i];
    var prev_vid = (i > 0 ? face_vertices[i-1] : face_vertices[face_vertices.length-1]);
    if (!direction_found) {
      if (vid == v2id && prev_vid == v1id) {
        direction_found = true;
        direction_is_correct = true;
      }
      else if (vid == v1id && prev_vid == v2id) {
        direction_found = true;
        direction_is_correct = false;
      }
    }
  }

  if (!direction_found) {
    console.log("Failed to align face " + face_to_align);
    return false;
  }

  if (direction_is_correct)
    return true;

  // If we reach this point, we need to reverse the direction of the list of edges of the face.
  var reversed_edge_array = [... f.edges];
  reversed_edge_array.reverse();
  f.edges = reversed_edge_array;
}

function vertex_pair_proceeds_in_right_order_around_normal(rfid, v1id, v2id) {

  var face_vertices = ordered_vertices_of_reference_face(rfid);

  var cofm = new THREE.Vector3(0,0,0);
  face_vertices.forEach(function(vid){
    cofm.add(uid_reference_vertices.getItem(vid).uid_object.object3d.position);
  });
  cofm.multiplyScalar(1/(face_vertices.length));

  // Calculate X-product from v1 to v2 wrt center of mass
  var dA = new THREE.Vector3();
  var dB = new THREE.Vector3();
  var n = new THREE.Vector3();
  dA.subVectors(uid_reference_vertices.getItem(v1id).uid_object.object3d.position, cofm);
  dB.subVectors(uid_reference_vertices.getItem(v2id).uid_object.object3d.position, cofm);
  n.crossVectors(dA, dB);

  // See if it matches normal
  return (n.dot(uid_reference_faces.getItem(rfid).normal) > 0);
}


function order_vertices_of_reference_face_around_normal(face_vertices, face_normal, vA, vB) {

  // Use face vertices to find center of mass
  var cofm = new THREE.Vector3(0,0,0);
  face_vertices.forEach(function(vid){
    cofm.add(uid_reference_vertices.getItem(vid).uid_object.object3d.position);
  });
  cofm.multiplyScalar(1/(face_vertices.length));

  // Calculate X-product from vA to vB wrt center of mass
  var dA = new THREE.Vector3();
  var dB = new THREE.Vector3();
  var n = new THREE.Vector3();
  dA.subVectors(uid_reference_vertices.getItem(vA).uid_object.object3d.position, cofm);
  dB.subVectors(uid_reference_vertices.getItem(vB).uid_object.object3d.position, cofm);
  n.crossVectors(dA, dB);

  // See if it matches normal
  if (n.dot(face_normal) > 0)
    return (vA);
  else
    return (vB);
}


function reference_face_has_one_connected_edge_loop(rf_id) {
  var rf = uid_reference_faces.getItem(rf_id);
  var es = [... rf.edges];

  // A donut-like object will have 0 edges, catch these types of objects.
  if (es.length == 0)
    return false;

  var connected_vertex_ids = [ uid_reference_edges.getItem(es[0]).vertices[0], uid_reference_edges.getItem(es[0]).vertices[1] ];
  var connected_edge_ids = [];
  var done = false;
  while (!done) {
    var n = connected_edge_ids.length;
    connected_vertex_ids.forEach(function(vid){
      es.forEach(function(eid){
        if ( (connected_edge_ids.indexOf(eid) == -1) &&
             (uid_reference_edges.getItem(eid).vertices[0] == vid || uid_reference_edges.getItem(eid).vertices[1] == vid))
          connected_edge_ids.push(eid);
      });
    });
    // Augment list of connected vertices with all vertices of current connected edges.
    connected_edge_ids.forEach(function(eid){
      if (connected_vertex_ids.indexOf(uid_reference_edges.getItem(eid).vertices[0]) == -1)
        connected_vertex_ids.push(uid_reference_edges.getItem(eid).vertices[0]);
      if (connected_vertex_ids.indexOf(uid_reference_edges.getItem(eid).vertices[1]) == -1)
        connected_vertex_ids.push(uid_reference_edges.getItem(eid).vertices[1]);
    });
    if (connected_edge_ids.length == n)
      done = true;
  }

  if (connected_edge_ids.length < es.length)
    return false;
  else
    return true;
}


function dump_imported_model_description_to_string(
  tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces, tmp_reference_bodies,
  force_everything_in_single_body, inward_looking_converted_face_label, outward_looking_converted_face_label,
  error_message_strings) {

  var model_ok = true;

  // Generate a new ID for the 2nd face, in case we are in force_everything_in_single_body mode
  var first_face_id;
  var second_face_id;
  if (force_everything_in_single_body) {
    first_face_id = uid_reference_faces.getItem(tmp_reference_faces[0]).id;
    second_face_id = get_global_id();
    // Also mark the ID of the inward facing face so we can subsequently pop-it-out.
    outward_looking_converted_face_label.v = first_face_id;
    inward_looking_converted_face_label.v = second_face_id;
  }

  var phi_string = '';

  phi_string += '<case_description><version>10</version><scale_fac>' + uil_get_world_scaling_factor() + '</scale_fac>';

  phi_string += '<bodies>';
  tmp_reference_bodies.forEach(function(b_label) {

    // Check the body is not malformed (e.g. consisting of open surfaces), by making sure that each edge has 2 faces on it.
    var body_ok = true;
    tmp_reference_edges.forEach(function(re_id) {
      var re = uid_reference_edges.getItem(re_id);
      if (re.vertices.length != 2 || re.faces.length != 2)
        model_ok = false;
    });
    if (!model_ok) {
      error_message_strings.push("Found non-brep body, possibly due to open surfaces. Unable to proceed.");
      return;
    }

    if (!force_everything_in_single_body)
      phi_string += '<body label="' + b_label + '"><id>' + uid_reference_bodies.getItem(b_label) + '</id>';
    else
      phi_string += '<body label="' + b_label + '"><id>' + b_label + '</id>';
    phi_string += '<face_ids>';

    var i = 0; // A counter so we can identify the 2nd face
    tmp_reference_faces.forEach(function(rf_id){
      var rf = uid_reference_faces.getItem(rf_id);
      if (!force_everything_in_single_body) {
        if (rf.onshape_body_id == b_label) {
          phi_string += '<face_id>' + rf.id + '</face_id>';
        }
      }
      else {
        if (i == 1 )
          phi_string += '<face_id>' + second_face_id + '</face_id>';
        else
          phi_string += '<face_id>' + rf.id + '</face_id>';
      }
      i++;
    });
    phi_string += '</face_ids>';
    phi_string += '<open>0</open></body>';
  });
  if (!model_ok) {

    return null;
  }

  phi_string += "</bodies>";

  phi_string += "<faces>";
  var i = 0;
  tmp_reference_faces.forEach(function(rf_id) {
    var rf = uid_reference_faces.getItem(rf_id);
    if (!force_everything_in_single_body)
      phi_string += '<face label="' + rf.onshape_id + '"><id>' + rf.id + '</id><body_id>' + uid_reference_bodies.getItem(rf.onshape_body_id) + '</body_id>';
    else {
      if (force_everything_in_single_body && i == 1 )
        phi_string += '<face label="' + second_face_id + '"><id>' + second_face_id + '</id><body_id>' + tmp_reference_bodies[0] + '</body_id>';
      else
        phi_string += '<face label="' + rf.onshape_id + '"><id>' + rf.id + '</id><body_id>' + tmp_reference_bodies[0] + '</body_id>';
    }

    phi_string += '<edge_ids>';

    if (force_everything_in_single_body) {
      if (i == 1) {
        // Add edges in the reverse order
        var earr = [... rf.edges];
        earr.reverse();
        earr.forEach(function(eid) {
          phi_string += '<edge_id>' + uid_reference_edges.getItem(eid).id + '</edge_id>';
        });
      }
      else {
        rf.edges.forEach(function(eid) {
          phi_string += '<edge_id>' + uid_reference_edges.getItem(eid).id + '</edge_id>';
        });
      }
    }
    else {
      rf.edges.forEach(function(eid) {
        phi_string += '<edge_id>' + uid_reference_edges.getItem(eid).id + '</edge_id>';
      });
    }
    phi_string += '</edge_ids>';

    phi_string += '<crib_gamma>29.000000</crib_gamma>';

    phi_string += '<visible>1</visible>';
    phi_string += '<fixed>0</fixed>';

    if (force_everything_in_single_body && i == 1 )
      phi_string += '<label>' + second_face_id + '</label>';
    else
      phi_string += '<label>' + rf.onshape_id + '</label>';

    phi_string += '<R>-1</R><G>-1</G><B>-1</B>';

    phi_string += '<material></material>';

    phi_string += '</face>';
    i++;
  });
  phi_string += "</faces>";

  phi_string += '<edges>';
  tmp_reference_edges.forEach(function(re_id) {
    var re = uid_reference_edges.getItem(re_id);
    phi_string += '<edge label="' + re.onshape_id + '"><id>' + re.id + '</id>';
    phi_string += '<vertex1_id>' + uid_reference_vertices.getItem(re.vertices[0]).id + '</vertex1_id>';
    phi_string += '<vertex2_id>' + uid_reference_vertices.getItem(re.vertices[1]).id + '</vertex2_id>';
    if (!force_everything_in_single_body) {
      phi_string += '<face1_id>' + uid_reference_faces.getItem(re.faces[0]).id + '</face1_id>';
      phi_string += '<face2_id>' + uid_reference_faces.getItem(re.faces[1]).id + '</face2_id>';
    }
    else {
      phi_string += '<face1_id>' + first_face_id + '</face1_id>';
      phi_string += '<face2_id>' + second_face_id + '</face2_id>';
    }
    phi_string += '<curve><id>' + get_global_id() + '</id>';

    // First of all, we remove every other sample except the first and last from the re.points array, as it comes in as
    // [p1, p2, p2, p3, p3, p4, p4, p5.... pn-1, pn]

    var arr = [];
    arr = [... re.points];

    if (arr.length < 2) {
      model_ok = false;
      var error_message = 'Edge ' + re.onshape_id + ' has minimal length (all points coincident). Interrupting operation.';
      error_message_strings.push(error_message);
      return null;
    }

    var approximate_v0 = new THREE.Vector3();
    var approximate_v1 = new THREE.Vector3();

    if (arr.length == 2) {
      approximate_v0.subVectors(arr[1], arr[0]);
      approximate_v1.subVectors(arr[1], arr[0]);
    }
    else {
      for (var i = arr.length-3; i > 0; i -= 2) {
        arr.splice(i,1);
      }

      // Set v0 and v1 vectors.
      var v_last = arr[arr.length-1];
      var v_one_before_last = arr[arr.length-2];
      var v_first = arr[0];
      var v_second = arr[1];

      if (v_second == null || v_first == null || v_last == null || v_one_before_last == null) {
        var xxx = 0;
      }

      approximate_v0.subVectors(v_second, v_first);
      approximate_v1.subVectors(v_last, v_one_before_last);
    }

    var va = new THREE.Vector3();
    var vb = new THREE.Vector3();
    va.copy(re.v0);
    vb.copy(re.v1);

    if (approximate_v0.dot(va) > approximate_v0.dot(vb) ) {
      phi_string += '<v0><vector3d><x>' + va.x + '</x><y>' + va.y + '</y><z>' + va.z + '</z></vector3d></v0>'; // XXX
      phi_string += '<v1><vector3d><x>' + vb.x + '</x><y>' + vb.y + '</y><z>' + vb.z + '</z></vector3d></v1>'; // XXX
    }
    else {
      phi_string += '<v0><vector3d><x>' + vb.x + '</x><y>' + vb.y + '</y><z>' + vb.z + '</z></vector3d></v0>'; // XXX
      phi_string += '<v1><vector3d><x>' + va.x + '</x><y>' + va.y + '</y><z>' + va.z + '</z></vector3d></v1>'; // XXX
    }
    phi_string += '<g0><vector3d><x>' + 0 + '</x><y>' + 0 + '</y><z>' + 0 + '</z></vector3d></g0>'; // XXX
    phi_string += '<g1><vector3d><x>' + 0 + '</x><y>' + 0 + '</y><z>' + 0 + '</z></vector3d></g1>'; // XXX
    phi_string += '<interpts>';
    var id = 1;

    // Make sure we have enough samples in the "points" array. Set limit to 10

    // Only resampling input array if less than 5 samples
    var done_here = false;
    while (arr.length < 5 && !done_here) {
      var length_before = arr.length;
      arr = fill_up_sample_array(arr);
      if (arr.length == length_before)
        // Failed to add more samples, we're done here
        done_here = true;
    }
    //

    //console_log("Reference edge with ID: " + id + " has " + arr.length + " samples.");

    // Replace the first and last samples with the vertex coordinates to make sure there are no discrepancies.
    if ( arr[0].distanceTo(uid_reference_vertices.getItem(re.vertices[0]).vertex) <
          arr[0].distanceTo(uid_reference_vertices.getItem(re.vertices[1]).vertex)) {
      arr[0].copy(uid_reference_vertices.getItem(re.vertices[0]).vertex);
      arr[arr.length-1].copy(uid_reference_vertices.getItem(re.vertices[1]).vertex);
    }
    else {
      arr[0].copy(uid_reference_vertices.getItem(re.vertices[1]).vertex);
      arr[arr.length-1].copy(uid_reference_vertices.getItem(re.vertices[0]).vertex);
    }

    // Make sure that we don't have any samples too close to the 1st/last one

    var local_epsilon = 1e-4;

    var done = false;
    while (!done && arr.length > 2) {
      if (maths.proximate(arr[1].distanceTo(arr[0]), 0, local_epsilon))
        arr.splice(1,1);
      else
        done = true;
    }
    done = false;
    while (!done && arr.length > 2) {
      if (maths.proximate(arr[arr.length-1].distanceTo(arr[arr.length-2]), 0, local_epsilon))
        arr.splice(arr.length-2,1);
      else
        done = true;
    }

    if (arr.length < 5) {
      model_ok = false;
      var error_message = 'Edge ' + re.onshape_id + ' has very short length. Interrupting operation.';
      error_message_strings.push(error_message);
    }

    arr.forEach(function(p){
      //add_small_ball(p.x, p.y, p.z, 255, 0, 0, 0.1);
      phi_string += '<interpt>';
      phi_string += '<id>' + id++ + '</id><coords><vector3d>';
      phi_string += '<x>'+ p.x + '</x><y>'+ p.y + '</y><z>'+ p.z + '</z>';
      phi_string += '</vector3d></coords>';
      phi_string += '</interpt>';
    });
    phi_string += '</interpts>';
    phi_string += '<nsim>12</nsim>';
    phi_string += '</curve>';
    phi_string += '<smoothness_flag>0</smoothness_flag>';
    phi_string += '<smoothness>-1</smoothness>';
    phi_string += '<linear>0</linear>';
    phi_string += '<visible>1</visible>';
    phi_string += '<fixed>0</fixed>';
    phi_string += '</edge>';
  });
  phi_string += '</edges>';

  phi_string += '<vertices>';
  tmp_reference_vertices.forEach(function(rv_id){
    var rv = uid_reference_vertices.getItem(rv_id);
    if (rv.edges.length > 0) {
      // We reject vertices with no edges on them
      phi_string += '<vertex label="' + rv.onshape_id + '"><id>' + rv.id + '</id>';
      phi_string += '<coords><vector3d><x>' + rv.vertex.x + '</x><y>' + rv.vertex.y + '</y><z>' + rv.vertex.z + '</z></vector3d></coords>';
      phi_string += '<normal><vector3d><x>0</x><y>0</y><z>0</z></vector3d></normal>';
      phi_string += '<smoothness_flag>0</smoothness_flag><smoothness>0</smoothness>';
      phi_string += '<edge_ids>';

      /* We saw that Onshape passes the edges in either random or reverse order. We'll handle this in two steps. First we will
         order them in a loop around the vertex, and then we will check if we need to reverse the order. First the loop. */
      var edges_array = [... rv.edges];
      var face_edges = new HashTable();
      var edge_faces = new HashTable();

      if (!force_everything_in_single_body) {

        // Create a list of all faces sharing this edge (and vertex)
        tmp_reference_faces.forEach(function(rf_id) {
          var rf = uid_reference_faces.getItem(rf_id);
          var face_contains_edge = false;
          rf.edges.forEach(function(re){
            if (edges_array.indexOf(re) != -1) {
              face_contains_edge = true;
              var a = face_edges.getItem(rf.uid_object.id);
              if (a)
                a.push(re);
              else
                a = [re];
              face_edges.setItem(rf.uid_object.id, a);

              a = edge_faces.getItem(re);
              if (a)
                a.push(rf.uid_object.id);
              else
                a = [rf.uid_object.id];
              edge_faces.setItem(re, a);
            }
          });
        });

        // Start from first edge.
        var sorted_edges = [];
        var current_edge = face_edges.firstValue()[0];
        var current_face = edge_faces.getItem(current_edge)[0];
        edges_array.splice(edges_array.indexOf(current_edge),1);
        sorted_edges.push(current_edge);

        while (edges_array.length > 0) {
          if (current_edge == face_edges.getItem(current_face)[0])
            current_edge = face_edges.getItem(current_face)[1];
          else
            current_edge = face_edges.getItem(current_face)[0];
          if (current_face == edge_faces.getItem(current_edge)[0])
            current_face = edge_faces.getItem(current_edge)[1];
          else
            current_face = edge_faces.getItem(current_edge)[0];

          // PROCESS THEM
          edges_array.splice(edges_array.indexOf(current_edge),1);
          sorted_edges.push(current_edge);
        }

        /* We may need to reverse the order of edges in rv.edges, so we must make sure that they go around in a
          direction pointing outwards according to the right-hand rule. To do this, we take the first two consecutive
          edges, then look for them in another face and make sure they appear in the opposite order. */

        var arr = sorted_edges;
        var must_reverse = false;
        var found_pair = false;
        var e1 = arr[0];
        var e2 = arr[1];
        tmp_reference_faces.forEach(function(rf_id) {
          var rf = uid_reference_faces.getItem(rf_id);
          for (var i = 0; i < rf.edges.length && !found_pair; i++) {
            var ii = i < rf.edges.length-1 ? i+1 : 0;
            if (rf.edges[i] == e1 && rf.edges[ii] == e2) {
              found_pair = true;
              must_reverse = true;
              console_log("Must reverse " + rv.id);
            }
            else if (rf.edges[i] == e2 && rf.edges[ii] == e1) {
              found_pair = true;
              must_reverse = false;
              console_log("Must not reverse " + rv.id);
            }
          }
        });

        if (must_reverse) {
          console_log("Reversing array:" + arr);
          arr.reverse();
          console_log("Reversed array is:" + arr);
        }

      }
      else {
        // Just check if the edges in rv.edges belong in the first face.
        var face1 = uid_reference_faces.getItem(tmp_reference_faces[0]);
        var arr = [];
        rv.edges.forEach(function(eid){
          if (face1.edges.indexOf(eid) != -1 ) {
            arr.push(eid);
          }
        });
        arr = [... new Set(arr)];
      }

      arr.forEach(function(e){
        phi_string += '<edge_id>' + uid_reference_edges.getItem(e).id + '</edge_id>';

      });
      phi_string += '</edge_ids>';
      phi_string += '<smooth_edge_pairs></smooth_edge_pairs>';
      phi_string += '<visible>1</visible>';
      phi_string += '<fixed>0</fixed>';
      phi_string += '</vertex>';
    }
  });

  if (!model_ok) {
    console_log_xml("Aborted generating phi model from onshape part studio");
    return null;
  }

  phi_string += '</vertices>';
  phi_string += '<extra_data_len>0</extra_data_len><extra_data></extra_data>';

  phi_string += '</case_description>';

  // Prepend header to the string
  var complete_string = "Phi;00001;000000005f747f0e;0001;0001;0001;000;;0001;";
  var n = decimalToHex(phi_string.length, 16);
  complete_string += n + ";" + n + ";" + phi_string;

  console_log_xml("COMPLETE PHI STRING:");
  console_log_xml(complete_string);
  return complete_string;
}
